import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UserButton } from "@clerk/clerk-react";
import { Pencil, ShieldCheck, Loader2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { Wordmark } from "@/components/Brand";
import { TaskRow } from "@/pages/AdminQueue";
import ConnectTikTok from "@/components/ConnectTikTok";

const PLACEMENT_STATUS_LABELS = {
    draft_pending: "Draft pending",
    draft_ready: "Draft ready",
    published: "Published",
    published_not_verified: "Published, not verified",
    live_verified: "Live verified",
    live_not_indexed: "Live, not indexed",
    needs_redo: "Needs redo",
};
const PLACEMENT_LIVE_STATUSES = new Set(["live_verified", "live_not_indexed"]);
const PLACEMENT_DRAFT_STATUSES = new Set(["draft_pending", "draft_ready"]);

function Row({ label, children, testid, href }) {
    const inner = (
        <>
            <div className="samoony-label pt-1 min-w-[110px]">{label}</div>
            <div className="text-right font-display text-base sm:text-lg text-ink flex-1 break-words">
                {children}
            </div>
        </>
    );
    const base = "py-4 border-b border-black/10 last:border-b-0 flex items-start justify-between gap-6";
    if (href) {
        return (
            <a
                data-testid={testid}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${base} min-h-11 hover:bg-black/[0.02] -mx-5 sm:-mx-6 px-5 sm:px-6 transition-colors`}
            >
                {inner}
            </a>
        );
    }
    return (
        <div data-testid={testid} className={base}>
            {inner}
        </div>
    );
}

// Shared waiting tile shown to a creator whose ownership is pending staff
// confirmation. Mobile-first, ink surface, sentence case, no em dashes.
export function PendingOwnershipTile({ testid = "pending-ownership-tile" }) {
    return (
        <div
            data-testid={testid}
            className="rounded-2xl px-5 py-6 sm:px-6 sm:py-7"
            style={{
                background: "var(--color-ink)",
                color: "var(--color-paper)",
            }}
        >
            <div
                className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase mb-3"
                style={{ color: "var(--color-paper)", opacity: 0.6 }}
            >
                Account status
            </div>
            <h2 className="font-display font-black tracking-tight text-[32px] sm:text-[36px] leading-[1.05] mb-3">
                Almost there
            </h2>
            <p className="text-[15px] sm:text-base leading-[1.5]" style={{ opacity: 0.85 }}>
                We're confirming this account is yours. This usually takes less than a day. Check back here soon.
            </p>
        </div>
    );
}

export default function Profile() {
    const { request } = useApi();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [creator, setCreator] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [publicBaseUrl, setPublicBaseUrl] = useState("");
    const [isRealDomain, setIsRealDomain] = useState(false);

    useEffect(() => {
        let active = true;
        request("/me")
            .then((res) => {
                if (!active) return;
                if (!res?.creator) {
                    // Admin accounts do not need a creator record. Land them
                    // on /admin/creators instead of onboarding.
                    if (res?.is_admin) {
                        navigate("/admin/creators", { replace: true });
                    } else {
                        navigate("/onboarding", { replace: true });
                    }
                    return;
                }
                setCreator(res.creator);
                setIsAdmin(!!res.is_admin);
                setPublicBaseUrl(res.public_base_url || "");
                setIsRealDomain(!!res.is_real_domain);
                // Approved creators who land on /profile after onboarding go
                // to the dashboard. /account renders this same page and must
                // stay put, since the dashboard links to it.
                if (
                    pathname === "/profile" &&
                    res.creator?.approved_at &&
                    !res.creator?.is_demo
                ) {
                    navigate("/dashboard", { replace: true });
                }
            })
            .catch(() => {})
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [request, navigate, pathname]);

    if (loading) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center text-sm text-black/50">
                Loading your profile...
            </div>
        );
    }

    if (!creator) return null;

    const handles = creator.handles || {};
    const publicPageHref =
        isRealDomain && publicBaseUrl
            ? `${publicBaseUrl}/@${creator.samoony_handle}`
            : `/@${creator.samoony_handle}`;
    const cityLabel = creator.region
        ? `${creator.city}, ${creator.region}`
        : creator.city;
    const tiktokUrls = creator.tiktok_video_urls || [];
    const isPendingStaff =
        creator.ownership_status === "pending_staff" && !creator.approved_at;

    // Pending-ownership creators (TikTok-only / YouTube-only awaiting staff
    // confirmation) see a simple waiting tile instead of the normal profile.
    // No build button, no review, no draft until staff approve them.
    if (isPendingStaff && !isAdmin) {
        return (
            <div className="min-h-screen bg-paper">
                <header className="flex items-center justify-between px-5 py-4 sm:px-8 sm:py-6 max-w-xl mx-auto">
                    <Link to="/" className="no-underline">
                        <Wordmark height={26} />
                    </Link>
                    <UserButton afterSignOutUrl="/" />
                </header>
                <main className="px-5 pb-16 sm:px-8 max-w-xl mx-auto">
                    <PendingOwnershipTile testid="profile-pending-ownership" />
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-paper">
            <header className="flex items-center justify-between px-5 py-4 sm:px-8 sm:py-6 max-w-xl mx-auto">
                <Link to="/" className="no-underline">
                    <Wordmark height={26} />
                </Link>
                <div className="flex items-center gap-2">
                    {isAdmin && (
                        <Link
                            to="/admin/creators"
                            data-testid="profile-admin-link"
                            className="samoony-chip"
                        >
                            Admin
                        </Link>
                    )}
                    {creator?.approved_at && !creator?.is_demo && (
                        <Link
                            to="/dashboard"
                            data-testid="account-dashboard-link"
                            className="samoony-pill samoony-pill-ghost text-sm"
                        >
                            Dashboard
                        </Link>
                    )}
                    <UserButton afterSignOutUrl="/" />
                </div>
            </header>

            <main className="px-5 pb-16 sm:px-8 max-w-xl mx-auto">
                <div className="samoony-label mb-3">Your account</div>
                <div className="flex items-start justify-between gap-4 mb-3">
                    <h1
                        data-testid="profile-display-name"
                        className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[0.95]"
                    >
                        {creator.display_name}
                    </h1>
                    <Link
                        to="/onboarding"
                        data-testid="profile-edit-link"
                        className="samoony-chip"
                    >
                        <Pencil size={14} /> Edit
                    </Link>
                </div>

                <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase mt-6 mb-2" style={{ color: "var(--color-muted)" }}>
                    Your page
                </div>

                <div
                    data-testid="profile-samoony-url"
                    className="font-mono text-sm text-black/70 mb-4 break-all"
                >
                    {((publicBaseUrl || "").replace(/^https?:\/\/(www\.)?/, "") || "samoony.com")}/@{creator.samoony_handle}
                </div>

                {creator.approved_at ? (
                    <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                        <a
                            data-testid="profile-view-page"
                            href={publicPageHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="samoony-pill samoony-pill-primary w-full text-center"
                        >
                            View page
                        </a>
                        <button
                            data-testid="profile-copy-link"
                            type="button"
                            onClick={() => {
                                try {
                                    navigator.clipboard.writeText(publicPageHref);
                                    toast.success("Link copied");
                                } catch {
                                    toast.error("Could not copy link.");
                                }
                            }}
                            className="samoony-pill samoony-pill-ghost w-full"
                        >
                            Copy link
                        </button>
                    </div>
                ) : (
                    <div
                        data-testid="profile-page-building"
                        className="samoony-pill samoony-pill-ghost mb-6"
                    >
                        Your page is being built
                    </div>
                )}

                <div
                    data-testid="profile-card"
                    className="bg-white border border-black/10 rounded-2xl px-5 sm:px-6 py-2"
                >
                    <Row label="Samoony handle" testid="profile-samoony-handle">
                        <span className="mono">@{creator.samoony_handle}</span>
                    </Row>
                    {creator.first_name && (
                        <Row label="First name" testid="profile-first-name">
                            {creator.first_name}
                        </Row>
                    )}
                    {creator.creator_name && (
                        <Row label="Creator name" testid="profile-creator-name">
                            {creator.creator_name}
                        </Row>
                    )}
                    <Row label="Age check" testid="profile-is-adult">
                        {creator.is_adult ? (
                            <span className="inline-flex items-center gap-1.5 text-ink">
                                <ShieldCheck size={16} className="text-signal" />
                                18 or older, confirmed
                            </span>
                        ) : (
                            <span className="text-black/40">Not confirmed</span>
                        )}
                    </Row>
                    <Row label="Primary niche" testid="profile-primary-niche">
                        <span className="inline-flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-signal" />
                            {creator.primary_niche}
                        </span>
                    </Row>
                    <Row label="Niches" testid="profile-niches">
                        <div className="flex flex-wrap gap-2 justify-end">
                            {(creator.niches || []).map((n) => (
                                <span
                                    key={n}
                                    data-primary={n === creator.primary_niche}
                                    className="samoony-chip !min-h-0 py-1"
                                >
                                    {n}
                                </span>
                            ))}
                        </div>
                    </Row>
                    <Row label="City" testid="profile-city">
                        {cityLabel}
                        {creator.country && (
                            <div className="text-xs text-black/50 font-sans">
                                {creator.country}
                            </div>
                        )}
                        {!creator.country && !creator.region && (
                            <div className="text-xs text-black/40 font-sans">
                                Saved as typed
                            </div>
                        )}
                    </Row>
                    <Row
                        label="Instagram"
                        testid="profile-instagram"
                        href={handles.instagram ? `https://www.instagram.com/${handles.instagram}/` : undefined}
                    >
                        {handles.instagram ? (
                            <span className="mono">@{handles.instagram}</span>
                        ) : (
                            <span className="text-black/40">Not added</span>
                        )}
                    </Row>
                    <Row
                        label="TikTok"
                        testid="profile-tiktok"
                        href={handles.tiktok ? `https://www.tiktok.com/@${handles.tiktok}` : undefined}
                    >
                        {handles.tiktok ? (
                            <span className="mono">@{handles.tiktok}</span>
                        ) : (
                            <span className="text-black/40">Not added</span>
                        )}
                    </Row>
                    <Row label="TikTok account" testid="profile-tiktok-connect">
                        <div className="flex justify-end text-left font-sans text-sm">
                            <ConnectTikTok compact />
                        </div>
                    </Row>
                    <Row
                        label="YouTube"
                        testid="profile-youtube"
                        href={handles.youtube ? `https://www.youtube.com/@${handles.youtube}` : undefined}
                    >
                        {handles.youtube ? (
                            <span className="mono">@{handles.youtube}</span>
                        ) : (
                            <span className="text-black/40">Not added</span>
                        )}
                    </Row>
                    <Row
                        label="LinkedIn"
                        testid="profile-linkedin"
                        href={handles.linkedin ? `https://www.linkedin.com/in/${handles.linkedin}/` : undefined}
                    >
                        {handles.linkedin ? (
                            <span className="mono">in/{handles.linkedin}</span>
                        ) : (
                            <span className="text-black/40">Not added</span>
                        )}
                    </Row>
                    <Row label="Link in bio" testid="profile-link-in-bio">
                        {creator.link_in_bio ? (
                            <a
                                href={creator.link_in_bio}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline decoration-signal underline-offset-4 break-all"
                            >
                                {creator.link_in_bio}
                            </a>
                        ) : (
                            <span className="text-black/40">Not added</span>
                        )}
                    </Row>
                    {tiktokUrls.length > 0 && (
                        <Row label="TikTok videos" testid="profile-tiktok-videos">
                            <ul className="space-y-1">
                                {tiktokUrls.map((u) => (
                                    <li key={u}>
                                        <a
                                            href={u}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mono text-sm underline decoration-signal underline-offset-4 break-all"
                                        >
                                            {u}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </Row>
                    )}
                </div>

                <div className="mt-8" />

                <UpdateNoticesSection publicPageHref={publicPageHref} />
            </main>
        </div>
    );
}

// ---------------- Update notices ----------------
function UpdateNoticesSection({ publicPageHref }) {
    const { request } = useApi();
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const res = await request("/notices");
            setNotices(res?.notices || []);
        } catch {
            // noop
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        let active = true;
        request("/notices")
            .then((res) => active && setNotices(res?.notices || []))
            .catch(() => {})
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const undo = async (id) => {
        if (busy) return;
        setBusy(true);
        try {
            await request(`/notices/${id}/undo`, { method: "POST" });
            toast.success("Previous version restored.");
            await load();
        } catch (err) {
            toast.error(err?.message || "Could not undo.");
        } finally {
            setBusy(false);
        }
    };
    const view = async (id) => {
        try {
            await request(`/notices/${id}/view`, { method: "POST" });
        } catch {
            // noop
        }
    };
    if (loading || notices.length === 0) return null;
    return (
        <section
            data-testid="update-notices-section"
            className="mb-8 bg-white border border-black/10 rounded-2xl px-5 py-4"
        >
            <div className="samoony-label mb-2">Updates</div>
            <ul className="space-y-3">
                {notices.map((n) => (
                    <li key={n.id} className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="font-display text-base text-ink">
                                Your page was updated
                            </div>
                            <div className="text-sm text-black/70">{n.summary}</div>
                            <div className="font-mono text-[11px] text-black/50 mt-0.5">
                                {new Date(n.created_at).toLocaleString()}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <a
                                data-testid={`notice-view-${n.id}`}
                                href={publicPageHref || "/profile"}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => view(n.id)}
                                className="samoony-pill samoony-pill-ghost"
                            >
                                See changes
                            </a>
                            <button
                                data-testid={`notice-undo-${n.id}`}
                                onClick={() => undo(n.id)}
                                disabled={busy}
                                className="samoony-pill samoony-pill-ghost disabled:opacity-30"
                            >
                                Undo
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
}

// ---------------- Admin: sources section ----------------
// Lives inside AdminCreatorPanel. Shows the stored sources (Instagram
// read-only, YouTube handle, TikTok handle, LinkedIn profile, TikTok video
// URLs, LinkedIn text) and lets an admin edit everything but Instagram.
// LinkedIn has no read API, so staff paste the headline, About and posts. Saves via POST /admin/creators/{id}
// /sources which runs TikTok URLs through oEmbed + author match and saves
// only matched URLs. No build is triggered.
export function AdminSourcesSection({ creator }) {
    const { request } = useApi();
    const initialYt = (creator?.handles?.youtube || "").trim();
    const initialTk = (creator?.handles?.tiktok || "").trim();
    const initialLi = (creator?.handles?.linkedin || "").trim();
    const initialLiText = creator?.linkedin_profile_text || "";
    const initialLiPosts = Array.isArray(creator?.linkedin_posts) ? creator.linkedin_posts : [];
    const initialSite = (creator?.website || "").trim();
    const initialUrls = Array.isArray(creator?.tiktok_video_urls)
        ? creator.tiktok_video_urls.join("\n")
        : "";
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(false);
    const [yt, setYt] = useState(initialYt);
    const [tk, setTk] = useState(initialTk);
    const [li, setLi] = useState(initialLi);
    const [liText, setLiText] = useState(initialLiText);
    const [liPosts, setLiPosts] = useState(initialLiPosts);
    const [site, setSite] = useState(initialSite);
    const [savedSite, setSavedSite] = useState(initialSite);
    const [siteCheck, setSiteCheck] = useState(creator?.website_check || null);
    const [urlsText, setUrlsText] = useState(initialUrls);
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);
    const [savedUrls, setSavedUrls] = useState(
        Array.isArray(creator?.tiktok_video_urls) ? creator.tiktok_video_urls : []
    );
    const [savedYt, setSavedYt] = useState(initialYt);
    const [savedTk, setSavedTk] = useState(initialTk);
    const [savedLi, setSavedLi] = useState(initialLi);
    const [savedLiText, setSavedLiText] = useState(initialLiText);
    const [savedLiPosts, setSavedLiPosts] = useState(initialLiPosts);
    const ig = (creator?.handles?.instagram || "").trim();

    const resetEditor = () => {
        setYt(savedYt);
        setTk(savedTk);
        setLi(savedLi);
        setLiText(savedLiText);
        setLiPosts(savedLiPosts);
        setSite(savedSite);
        setUrlsText(savedUrls.join("\n"));
        setResult(null);
    };

    const save = async () => {
        if (saving) return;
        const urls = urlsText
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean)
            .slice(0, 10);
        setSaving(true);
        setResult(null);
        try {
            const res = await request(
                `/admin/creators/${creator.id}/sources`,
                {
                    method: "POST",
                    body: {
                        youtube_handle: yt.trim(),
                        tiktok_handle: tk.trim(),
                        linkedin_handle: li.trim(),
                        linkedin_profile_text: liText,
                        linkedin_posts: liPosts
                            .map((x) => ({ url: (x.url || "").trim(), text: (x.text || "").trim() }))
                            .filter((x) => x.text),
                        website: site.trim(),
                        tiktok_video_urls: urls,
                    },
                }
            );
            setResult(res);
            const nextYt = res?.handles?.youtube || "";
            const nextTk = res?.handles?.tiktok || "";
            const nextLi = res?.handles?.linkedin || "";
            const nextLiText = res?.linkedin_profile_text || "";
            const nextLiPosts = Array.isArray(res?.linkedin_posts) ? res.linkedin_posts : [];
            const nextUrls = Array.isArray(res?.tiktok_video_urls)
                ? res.tiktok_video_urls
                : [];
            setSavedYt(nextYt);
            setSavedTk(nextTk);
            setSavedLi(nextLi);
            setSavedLiText(nextLiText);
            setSavedLiPosts(nextLiPosts);
            setSavedUrls(nextUrls);
            const nextSite = res?.website || "";
            setSavedSite(nextSite);
            setSite(nextSite);
            setSiteCheck(res?.website_check || null);
            setYt(nextYt);
            setTk(nextTk);
            setLi(nextLi);
            setLiText(nextLiText);
            setLiPosts(nextLiPosts);
            setUrlsText(nextUrls.join("\n"));
            const matched = (res?.rows || []).filter((r) => r.status === "matched").length;
            const skipped = (res?.rows || []).filter((r) => r.status === "skipped").length;
            if (skipped > 0) {
                toast.success(
                    `Sources saved. ${matched} TikTok URL${matched === 1 ? "" : "s"} matched, ${skipped} skipped.`
                );
            } else {
                toast.success("Sources saved.");
            }
        } catch (err) {
            toast.error(err?.message || "Could not save sources.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            data-testid="admin-sources-section"
            className="mt-3 border border-black/10 rounded-2xl bg-white"
        >
            <button
                type="button"
                data-testid="admin-sources-toggle"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left"
            >
                <div>
                    <div className="samoony-label">Sources</div>
                    <div className="font-mono text-[11px] text-black/60">
                        IG @{ig || "–"}
                        {savedTk ? ` · TT @${savedTk}` : ""}
                        {savedYt ? ` · YT @${savedYt}` : ""}
                        {savedLi ? ` · LI ${savedLi}` : ""}
                        {savedLi ? ` · ${savedLiPosts.length} LinkedIn post${savedLiPosts.length === 1 ? "" : "s"}` : ""}
                        {siteCheck?.status === "confirmed" ? " · website" : ""}
                        {" · "}
                        {savedUrls.length} TikTok URL{savedUrls.length === 1 ? "" : "s"}
                    </div>
                </div>
                <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-black/60">
                    {open ? "Hide" : "Open"}
                </span>
            </button>

            {open && (
                <div className="px-5 pb-4">
                    {!editing && (
                        <div className="flex flex-col gap-3">
                            <SourceRow
                                label="Instagram"
                                testid="admin-sources-view-instagram"
                                value={ig ? `@${ig}` : "empty"}
                                hint="Read only here. Changes to Instagram happen through onboarding."
                            />
                            <SourceRow
                                label="YouTube"
                                testid="admin-sources-view-youtube"
                                value={savedYt ? `@${savedYt}` : "empty"}
                            />
                            <SourceRow
                                label="TikTok"
                                testid="admin-sources-view-tiktok"
                                value={savedTk ? `@${savedTk}` : "empty"}
                            />
                            <SourceRow
                                label="LinkedIn"
                                testid="admin-sources-view-linkedin"
                                value={savedLi ? `linkedin.com/in/${savedLi}` : "empty"}
                            />
                            <SourceRow
                                label="LinkedIn headline and About"
                                testid="admin-sources-view-linkedin-text"
                                value={savedLiText ? `${savedLiText.length} characters` : "empty"}
                            />
                            <SourceRow
                                label="LinkedIn posts"
                                testid="admin-sources-view-linkedin-posts"
                                value={`${savedLiPosts.length} pasted`}
                            />
                            <SourceRow
                                label="Website"
                                testid="admin-sources-view-website"
                                value={savedSite || "empty"}
                                hint={websiteCheckHint(siteCheck)}
                            />
                            <div data-testid="admin-sources-view-tiktok-urls">
                                <div className="samoony-label mb-1">
                                    TikTok video URLs
                                </div>
                                {savedUrls.length === 0 ? (
                                    <div className="font-mono text-[12px] text-black/60">
                                        None
                                    </div>
                                ) : (
                                    <ul className="flex flex-col gap-1">
                                        {savedUrls.map((u, i) => (
                                            <li
                                                key={i}
                                                className="font-mono text-[12px] text-black/80 break-all"
                                            >
                                                {u}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div>
                                <button
                                    type="button"
                                    data-testid="admin-sources-edit"
                                    onClick={() => {
                                        resetEditor();
                                        setEditing(true);
                                    }}
                                    className="samoony-pill samoony-pill-ghost"
                                >
                                    Edit sources
                                </button>
                            </div>
                        </div>
                    )}

                    {editing && (
                        <div className="flex flex-col gap-3">
                            <SourceRow
                                label="Instagram"
                                testid="admin-sources-edit-instagram"
                                value={ig ? `@${ig}` : "empty"}
                                hint="Read only here."
                            />
                            <label className="flex flex-col gap-1">
                                <span className="samoony-label">
                                    YouTube handle
                                </span>
                                <input
                                    data-testid="admin-sources-input-youtube"
                                    type="text"
                                    value={yt}
                                    onChange={(e) => setYt(e.target.value)}
                                    placeholder="@creator"
                                    className="rounded-lg border border-black/10 px-3 py-2 text-sm bg-white"
                                />
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className="samoony-label">
                                    TikTok handle
                                </span>
                                <input
                                    data-testid="admin-sources-input-tiktok"
                                    type="text"
                                    value={tk}
                                    onChange={(e) => setTk(e.target.value)}
                                    placeholder="@creator"
                                    className="rounded-lg border border-black/10 px-3 py-2 text-sm bg-white"
                                />
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className="samoony-label">
                                    LinkedIn profile (URL or slug)
                                </span>
                                <input
                                    data-testid="admin-sources-input-linkedin"
                                    type="text"
                                    value={li}
                                    onChange={(e) => setLi(e.target.value)}
                                    placeholder="linkedin.com/in/creator"
                                    className="rounded-lg border border-black/10 px-3 py-2 text-sm bg-white"
                                />
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className="samoony-label">
                                    LinkedIn headline and About (pasted)
                                </span>
                                <textarea
                                    data-testid="admin-sources-input-linkedin-text"
                                    rows={5}
                                    value={liText}
                                    onChange={(e) => setLiText(e.target.value)}
                                    maxLength={3000}
                                    placeholder="Headline, location and About section, as shown on their profile"
                                    className="rounded-lg border border-black/10 px-3 py-2 text-sm bg-white"
                                />
                            </label>
                            <div
                                data-testid="admin-sources-input-linkedin-posts"
                                className="flex flex-col gap-2"
                            >
                                <span className="samoony-label">
                                    LinkedIn posts (pasted, up to 10; 3 or more for a LinkedIn-only page)
                                </span>
                                {liPosts.map((post, i) => (
                                    <div
                                        key={i}
                                        className="flex flex-col gap-1 rounded-lg border border-black/10 p-2"
                                    >
                                        <input
                                            type="text"
                                            value={post.url || ""}
                                            onChange={(e) =>
                                                setLiPosts(liPosts.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))
                                            }
                                            placeholder="https://www.linkedin.com/posts/..."
                                            className="rounded-lg border border-black/10 px-3 py-2 text-sm font-mono bg-white"
                                        />
                                        <textarea
                                            rows={4}
                                            value={post.text || ""}
                                            onChange={(e) =>
                                                setLiPosts(liPosts.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))
                                            }
                                            maxLength={3000}
                                            placeholder="Post text"
                                            className="rounded-lg border border-black/10 px-3 py-2 text-sm bg-white"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setLiPosts(liPosts.filter((_, j) => j !== i))}
                                            className="self-start font-mono text-[11px] uppercase tracking-[0.14em] text-black/60"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                                {liPosts.length < 10 && (
                                    <button
                                        type="button"
                                        data-testid="admin-sources-add-linkedin-post"
                                        onClick={() => setLiPosts([...liPosts, { url: "", text: "" }])}
                                        className="samoony-pill samoony-pill-ghost self-start"
                                    >
                                        Add LinkedIn post
                                    </button>
                                )}
                            </div>
                            <label className="flex flex-col gap-1">
                                <span className="samoony-label">
                                    Website (optional)
                                </span>
                                <input
                                    data-testid="admin-sources-input-website"
                                    type="text"
                                    value={site}
                                    onChange={(e) => setSite(e.target.value)}
                                    placeholder="https://creator.com"
                                    className="rounded-lg border border-black/10 px-3 py-2 text-sm font-mono bg-white"
                                />
                                <span className="font-mono text-[11px] text-black/50">
                                    Used when its pages name the creator. Left empty, we try their link in bio and Instagram bio links.
                                </span>
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className="samoony-label">
                                    TikTok video URLs (one per line, up to 10)
                                </span>
                                <textarea
                                    data-testid="admin-sources-input-tiktok-urls"
                                    rows={6}
                                    value={urlsText}
                                    onChange={(e) => setUrlsText(e.target.value)}
                                    placeholder="https://www.tiktok.com/@creator/video/123..."
                                    className="rounded-lg border border-black/10 px-3 py-2 text-sm font-mono bg-white"
                                />
                            </label>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    data-testid="admin-sources-save"
                                    onClick={save}
                                    disabled={saving}
                                    className="samoony-pill samoony-pill-primary disabled:opacity-30"
                                >
                                    {saving ? "Saving..." : "Save sources"}
                                </button>
                                <button
                                    type="button"
                                    data-testid="admin-sources-cancel"
                                    onClick={() => {
                                        resetEditor();
                                        setEditing(false);
                                    }}
                                    disabled={saving}
                                    className="samoony-pill samoony-pill-ghost"
                                >
                                    Cancel
                                </button>
                            </div>

                            {result && Array.isArray(result.rows) && result.rows.length > 0 && (
                                <ul
                                    data-testid="admin-sources-result"
                                    className="mt-2 flex flex-col gap-2"
                                >
                                    {result.rows.map((r, i) => (
                                        <li
                                            key={i}
                                            data-testid={`admin-sources-result-row-${i}`}
                                            className="border-t border-black/10 pt-2 first:border-t-0 first:pt-0 text-sm"
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span
                                                    className="font-mono text-[11px] uppercase tracking-[0.12em]"
                                                    style={{
                                                        color:
                                                            r.status === "matched"
                                                                ? "var(--color-ok)"
                                                                : "var(--color-signal)",
                                                    }}
                                                >
                                                    {r.status === "matched" ? "saved" : "skipped"}
                                                </span>
                                                <span className="font-mono text-[11px] text-black/60 truncate max-w-[55%]">
                                                    {r.url}
                                                </span>
                                            </div>
                                            {r.caption && (
                                                <div className="mt-1">{r.caption}</div>
                                            )}
                                            {r.status === "skipped" && r.reason && (
                                                <div className="mt-1 text-[12px] text-black/70">
                                                    {r.reason}
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

const WEBSITE_SOURCE_LABELS = {
    staff: "set here",
    link_in_bio: "link in bio",
    instagram_bio: "Instagram bio",
};

// One line on the last website check: which site, where it came from and
// whether its pages name the creator.
function websiteCheckHint(check) {
    if (!check || !check.status) return "Not checked yet. Checked at the next build.";
    if (check.status === "none") return "No website found in link in bio or Instagram bio.";
    const from = WEBSITE_SOURCE_LABELS[check.source] || check.source || "";
    const where = `${check.url || ""}${from ? ` (${from})` : ""}`;
    if (check.status === "confirmed") {
        return `On the page: ${where}. Matched ${check.matched_on || "the creator"}, ${check.pages || 0} page${check.pages === 1 ? "" : "s"} read.`;
    }
    if (check.status === "unconfirmed") return `Website not confirmed: ${where} does not name the creator.`;
    if (check.status === "blocked_by_robots") return `Website not read: ${where} blocks crawlers in robots.txt.`;
    return `Website not reached: ${where}.`;
}

function SourceRow({ label, value, hint, testid }) {
    return (
        <div data-testid={testid}>
            <div className="samoony-label mb-1">{label}</div>
            <div className="font-mono text-[12px] text-black/80">{value}</div>
            {hint && (
                <div className="font-mono text-[11px] text-black/50 mt-1">
                    {hint}
                </div>
            )}
        </div>
    );
}

// ---------------- Admin: placements section ----------------
// Collapsed by default. Header shows a counts summary ("N placements · M
// live · K draft"). When opened, reuses the same TaskRow + TaskDetail
// components the main Placement queue uses, filtered to this creator by
// calling /admin/placement/queue?creator_id={id}. When the creator has no
// tasks, exposes a "Create placement tasks" button that posts to the
// existing /admin/placement/create/{id} endpoint.

export function AdminDirectoryListingsSection({ creator }) {
    const { request } = useApi();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ platform: "", url: "", label: "" });
    const [editingUrl, setEditingUrl] = useState(null);

    const load = async () => {
        try {
            const res = await request(
                `/admin/creators/${creator.id}/directory-listings`,
                { method: "GET" }
            );
            setItems(res?.items || []);
        } catch (e) {
            toast.error("Could not load directory listings.");
        }
    };

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && items === null) load();
    };

    const add = async () => {
        if (!form.platform.trim() || !form.url.trim()) {
            toast.error("Platform and URL are required.");
            return;
        }
        setBusy(true);
        try {
            await request(`/admin/creators/${creator.id}/directory-listings`, {
                method: "POST",
                body: {
                    platform: form.platform.trim(),
                    url: form.url.trim(),
                    label: form.label.trim(),
                },
            });
            setForm({ platform: "", url: "", label: "" });
            await load();
            toast.success("Listing added.");
        } catch (e) {
            toast.error(e?.data?.detail || "Could not add listing.");
        } finally {
            setBusy(false);
        }
    };

    const save = async (url) => {
        const item = items.find((i) => i.url === url);
        if (!item) return;
        setBusy(true);
        try {
            await request(`/admin/creators/${creator.id}/directory-listings`, {
                method: "PATCH",
                body: {
                    platform: (item.platform || "").trim(),
                    url,
                    label: (item.label || "").trim(),
                },
            });
            setEditingUrl(null);
            await load();
            toast.success("Listing updated.");
        } catch (e) {
            toast.error(e?.data?.detail || "Could not update listing.");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (url) => {
        if (!window.confirm("Remove this directory listing?")) return;
        setBusy(true);
        try {
            await request(
                `/admin/creators/${creator.id}/directory-listings?url=${encodeURIComponent(url)}`,
                { method: "DELETE" }
            );
            await load();
            toast.success("Listing removed.");
        } catch (e) {
            toast.error(e?.data?.detail || "Could not remove listing.");
        } finally {
            setBusy(false);
        }
    };

    const count = items === null ? "…" : items.length;
    return (
        <div className="rounded-xl border border-ink/10 overflow-hidden" data-testid="admin-directory-listings-section">
            <button
                type="button"
                onClick={toggle}
                className="w-full flex items-center justify-between px-4 py-3 text-left bg-paper hover:bg-ink/5"
                data-testid="admin-directory-listings-toggle"
            >
                <span className="font-display font-bold">Directory listings</span>
                <span className="font-mono text-xs text-ink/60">{count} listed</span>
            </button>
            {open && (
                <div className="p-4 space-y-4 bg-white">
                    {items === null ? (
                        <p className="text-sm text-ink/60">Loading…</p>
                    ) : items.length === 0 ? (
                        <p className="text-sm text-ink/60" data-testid="admin-directory-listings-empty">
                            No listings yet.
                        </p>
                    ) : (
                        <ul className="space-y-3" data-testid="admin-directory-listings-list">
                            {items.map((it) => {
                                const isEditing = editingUrl === it.url;
                                return (
                                    <li
                                        key={it.url}
                                        className="rounded-lg border border-ink/10 p-3 space-y-2"
                                        data-testid={`admin-directory-listings-row-${it.url}`}
                                    >
                                        {isEditing ? (
                                            <>
                                                <input
                                                    className="w-full rounded border border-ink/20 px-2 py-1 text-sm"
                                                    value={it.platform || ""}
                                                    onChange={(e) =>
                                                        setItems((prev) =>
                                                            prev.map((p) =>
                                                                p.url === it.url
                                                                    ? { ...p, platform: e.target.value }
                                                                    : p
                                                            )
                                                        )
                                                    }
                                                    placeholder="Platform"
                                                    data-testid={`admin-directory-listings-edit-platform-${it.url}`}
                                                />
                                                <input
                                                    className="w-full rounded border border-ink/20 px-2 py-1 text-sm"
                                                    value={it.label || ""}
                                                    onChange={(e) =>
                                                        setItems((prev) =>
                                                            prev.map((p) =>
                                                                p.url === it.url
                                                                    ? { ...p, label: e.target.value }
                                                                    : p
                                                            )
                                                        )
                                                    }
                                                    placeholder="Label"
                                                    data-testid={`admin-directory-listings-edit-label-${it.url}`}
                                                />
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => save(it.url)}
                                                        disabled={busy}
                                                        className="samoony-pill"
                                                        data-testid={`admin-directory-listings-save-${it.url}`}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setEditingUrl(null);
                                                            load();
                                                        }}
                                                        className="samoony-pill-ghost"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <div className="font-display font-bold text-sm">
                                                            {it.platform}
                                                        </div>
                                                        <a
                                                            href={it.url}
                                                            target="_blank"
                                                            rel="noopener"
                                                            className="text-xs text-ink/60 break-all underline"
                                                        >
                                                            {it.url}
                                                        </a>
                                                        {it.label && (
                                                            <p className="text-xs text-ink/80 mt-1">
                                                                {it.label}
                                                            </p>
                                                        )}
                                                        <p className="text-[10px] font-mono uppercase text-ink/40 mt-1">
                                                            {it.source || "manual"}
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-2 shrink-0">
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingUrl(it.url)}
                                                            className="samoony-pill-ghost text-xs"
                                                            data-testid={`admin-directory-listings-edit-${it.url}`}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => remove(it.url)}
                                                            disabled={busy}
                                                            className="samoony-pill-ghost text-xs text-signal"
                                                            data-testid={`admin-directory-listings-remove-${it.url}`}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <div className="pt-3 border-t border-ink/10 space-y-2">
                        <p className="text-sm font-display font-bold">Add listing</p>
                        <input
                            className="w-full rounded border border-ink/20 px-2 py-1 text-sm"
                            value={form.platform}
                            onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))}
                            placeholder="Platform (for example Elev8or)"
                            data-testid="admin-directory-listings-add-platform"
                        />
                        <input
                            className="w-full rounded border border-ink/20 px-2 py-1 text-sm"
                            value={form.url}
                            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                            placeholder="https://..."
                            data-testid="admin-directory-listings-add-url"
                        />
                        <input
                            className="w-full rounded border border-ink/20 px-2 py-1 text-sm"
                            value={form.label}
                            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                            placeholder="Label (for example Creator profile on Elev8or)"
                            data-testid="admin-directory-listings-add-label"
                        />
                        <button
                            type="button"
                            onClick={add}
                            disabled={busy}
                            className="samoony-pill"
                            data-testid="admin-directory-listings-add-submit"
                        >
                            Add listing
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}


export function AdminPlacementsSection({ creator }) {
    const { request } = useApi();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tasks, setTasks] = useState([]);
    const [channels, setChannels] = useState([]);
    const [openTaskId, setOpenTaskId] = useState(null);
    const [creating, setCreating] = useState(false);
    const [loadedOnce, setLoadedOnce] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await request(
                `/admin/placement/queue?creator_id=${encodeURIComponent(creator.id)}`
            );
            setTasks(res?.tasks || []);
            setChannels(res?.channels || []);
            setLoadedOnce(true);
        } catch (err) {
            toast.error(err?.message || "Could not load placements.");
        } finally {
            setLoading(false);
        }
    }, [creator.id, request]);

    // Load counts (and the full list) the first time the section opens, and
    // whenever reloads are needed. Keeps the collapsed header truthful.
    useEffect(() => {
        if (!loadedOnce) {
            void load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-poll while any task is still generating, same as the main queue.
    useEffect(() => {
        if (!tasks.length) return;
        const anyRunning = tasks.some((t) => t.generation_status === "running");
        if (!anyRunning) return;
        const h = setTimeout(() => {
            void load();
        }, 4000);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tasks]);

    const liveCount = tasks.filter((t) => PLACEMENT_LIVE_STATUSES.has(t.status)).length;
    const draftCount = tasks.filter((t) => PLACEMENT_DRAFT_STATUSES.has(t.status)).length;
    const summary = `${tasks.length} placement${tasks.length === 1 ? "" : "s"} · ${liveCount} live · ${draftCount} draft`;

    const createTasks = async () => {
        if (creating) return;
        setCreating(true);
        try {
            const res = await request(`/admin/placement/create/${creator.id}`, {
                method: "POST",
            });
            if (res?.created > 0) {
                toast.success(
                    `Created ${res.created} placement task${res.created === 1 ? "" : "s"}.`
                );
            } else {
                toast.success(res?.message || "No tasks created.");
            }
            await load();
        } catch (err) {
            toast.error(err?.message || "Could not create placement tasks.");
        } finally {
            setCreating(false);
        }
    };

    return (
        <section
            data-testid="admin-placements-section"
            className="mt-3 border border-black/10 rounded-2xl bg-white"
        >
            <button
                type="button"
                data-testid="admin-placements-toggle"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left"
            >
                <div>
                    <div className="samoony-label">Placements</div>
                    <div
                        data-testid="admin-placements-summary"
                        className="font-mono text-[11px] text-black/60"
                    >
                        {loadedOnce ? summary : "Loading..."}
                    </div>
                </div>
                <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-black/60">
                    {open ? "Hide" : "Open"}
                </span>
            </button>

            {open && (
                <div className="px-5 pb-4">
                    {loading && !tasks.length && (
                        <div className="text-sm text-black/60">Loading...</div>
                    )}

                    {!loading && tasks.length === 0 && (
                        <div className="flex flex-col gap-3">
                            <div
                                data-testid="admin-placements-empty"
                                className="text-sm text-black/60"
                            >
                                No placement tasks yet for this creator.
                            </div>
                            <div>
                                <button
                                    type="button"
                                    data-testid="admin-placements-create"
                                    onClick={createTasks}
                                    disabled={creating}
                                    className="samoony-chip disabled:opacity-30"
                                >
                                    {creating ? "Creating..." : "Create placement tasks"}
                                </button>
                            </div>
                        </div>
                    )}

                    {tasks.length > 0 && (
                        <ul
                            data-testid="admin-placements-list"
                            className="border border-black/10 rounded-xl divide-y divide-black/10"
                        >
                            {tasks.map((t) => (
                                <TaskRow
                                    key={t.id}
                                    t={t}
                                    channels={channels}
                                    isOpen={openTaskId === t.id}
                                    onToggle={() =>
                                        setOpenTaskId(openTaskId === t.id ? null : t.id)
                                    }
                                    onChanged={load}
                                    showCreatorLink={false}
                                />
                            ))}
                        </ul>
                    )}

                    <ManualPlacementForm
                        creatorId={creator.id}
                        channels={channels}
                        onCreated={load}
                    />
                </div>
            )}
        </section>
    );
}

// Record an external win that the pipeline didn't produce (a second
// Feedspot list, a surprise LinkedIn feature). Posts to
// /admin/creators/:id/placements/manual; the task is tagged source="manual"
// and manual_locked=true so the regenerator never overwrites it.
function ManualPlacementForm({ creatorId, channels, onCreated }) {
    const { request } = useApi();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [channelId, setChannelId] = useState("");
    const [title, setTitle] = useState("");
    const [url, setUrl] = useState("");
    const [status, setStatus] = useState("");

    const options = (channels && channels.length)
        ? channels
        : [
            { id: "medium", label: "Medium" },
            { id: "linkedin", label: "LinkedIn" },
            { id: "substack", label: "Substack" },
            { id: "feedspot", label: "Feedspot" },
            { id: "manual", label: "Other" },
        ];

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        if (!channelId || !title.trim() || !url.trim() || !status) {
            toast.error("Pick a type, status, and fill title and URL.");
            return;
        }
        setBusy(true);
        try {
            const res = await request(
                `/admin/creators/${creatorId}/placements/manual`,
                {
                    method: "POST",
                    body: {
                        channel_id: channelId,
                        title: title.trim(),
                        url: url.trim(),
                        status,
                    },
                },
            );
            const finalStatus = res?.task?.status || status;
            const map = {
                live_verified: "Live verified.",
                live_not_indexed: "Live, not indexed.",
                published_not_verified: "Saved; fetch failed, use Recheck later.",
                published: "Published.",
                draft_ready: "Draft ready.",
                draft_pending: "Draft pending.",
            };
            toast.success(map[finalStatus] || "Placement saved.");
            setChannelId("");
            setTitle("");
            setUrl("");
            setStatus("");
            setOpen(false);
            onCreated?.();
        } catch (err) {
            toast.error(err?.message || "Could not record placement.");
        } finally {
            setBusy(false);
        }
    };

    if (!open) {
        return (
            <div className="mt-3">
                <button
                    type="button"
                    data-testid="admin-placements-add-manual-toggle"
                    onClick={() => setOpen(true)}
                    className="samoony-chip"
                >
                    Add placement manually
                </button>
            </div>
        );
    }

    return (
        <form
            data-testid="admin-placements-add-manual-form"
            onSubmit={submit}
            className="mt-3 bg-black/[0.03] rounded-xl p-3 flex flex-col gap-2"
        >
            <div className="flex items-center justify-between">
                <div className="samoony-label">Add placement manually</div>
                <button
                    type="button"
                    data-testid="admin-placements-add-manual-cancel"
                    onClick={() => setOpen(false)}
                    className="text-xs text-black/50 hover:text-ink"
                >
                    Cancel
                </button>
            </div>
            <label className="text-xs text-black/60">
                Type
                <select
                    data-testid="admin-placements-add-manual-type"
                    value={channelId}
                    onChange={(e) => setChannelId(e.target.value)}
                    className="samoony-input !h-9 w-full"
                >
                    <option value="">Select…</option>
                    {options.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.label}
                        </option>
                    ))}
                </select>
            </label>
            <label className="text-xs text-black/60">
                Title
                <input
                    data-testid="admin-placements-add-manual-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Article or list title"
                    className="samoony-input !h-9 w-full"
                />
            </label>
            <label className="text-xs text-black/60">
                URL
                <input
                    data-testid="admin-placements-add-manual-url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://..."
                    className="samoony-input !h-9 w-full"
                />
            </label>
            <label className="text-xs text-black/60">
                Status
                <select
                    data-testid="admin-placements-add-manual-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="samoony-input !h-9 w-full"
                >
                    <option value="">Pick a status…</option>
                    <option value="live_verified">
                        Live (run indexing check)
                    </option>
                    <option value="live_not_indexed">
                        Live, not indexed (run indexing check)
                    </option>
                    <option value="published">
                        Published (run indexing check)
                    </option>
                    <option value="draft_ready">Draft ready</option>
                    <option value="draft_pending">Draft pending</option>
                </select>
            </label>
            <div className="flex justify-end">
                <button
                    type="submit"
                    data-testid="admin-placements-add-manual-submit"
                    disabled={busy}
                    className="samoony-chip disabled:opacity-30"
                >
                    {busy ? "Saving..." : "Save placement"}
                </button>
            </div>
        </form>
    );
}

// Re-export the status label map so other components can read it without
// a second definition. (Not used by AdminQueue, which has its own copy.)
export const PlacementStatusLabels = PLACEMENT_STATUS_LABELS;

// ---------------- Admin: real-creator panel ----------------
export function AdminCreatorPanel({ creator, publicPageHref }) {
    const { request } = useApi();
    const [rebuilding, setRebuilding] = useState(false);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showAdminTracked, setShowAdminTracked] = useState(false);
    const [showAdminHandle, setShowAdminHandle] = useState(false);
    const [healthSubScores, setHealthSubScores] = useState(null);

    const loadStatus = async () => {
        try {
            const res = await request(`/admin/creator-status/${creator.id}`);
            setStatus(res || null);
        } catch {
            // noop
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await request(`/admin/dashboard/${creator.id}`);
                if (!cancelled) setHealthSubScores(res?.brand_health || null);
            } catch {
                // noop
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creator.id]);

    useEffect(() => {
        let cancelled = false;
        let timer = null;
        const tick = async () => {
            if (cancelled) return;
            try {
                const res = await request(`/admin/creator-status/${creator.id}`);
                if (cancelled) return;
                setStatus(res || null);
                const running = !!res?.job && !res.job.finished_at;
                timer = setTimeout(tick, running ? 3000 : 15000);
            } catch {
                timer = setTimeout(tick, 15000);
            }
        };
        void tick();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creator.id]);

    const rebuild = async () => {
        if (rebuilding) return;
        setRebuilding(true);
        // Optimistically show a running state so the panel updates immediately.
        setStatus((prev) => ({
            ...(prev || {}),
            job: {
                ...((prev && prev.job) || {}),
                stage: "starting",
                created_at: new Date().toISOString(),
                finished_at: null,
            },
        }));
        try {
            await request(`/admin/rebuild/${creator.id}`, { method: "POST" });
            toast.success("Rebuild started.");
            setTimeout(loadStatus, 1500);
        } catch (err) {
            toast.error(err?.message || "Could not start rebuild.");
        } finally {
            setRebuilding(false);
        }
    };

    const publishHeld = async (draftId) => {
        if (busy) return;
        setBusy(true);
        try {
            await request(`/admin/held/${draftId}/publish`, { method: "POST" });
            toast.success("Published.");
            await loadStatus();
        } catch (err) {
            toast.error(err?.message || "Could not publish.");
        } finally {
            setBusy(false);
        }
    };

    const discardHeld = async (draftId) => {
        if (busy) return;
        setBusy(true);
        try {
            await request(`/admin/held/${draftId}/discard`, { method: "POST" });
            toast.success("Discarded.");
            await loadStatus();
        } catch (err) {
            toast.error(err?.message || "Could not discard.");
        } finally {
            setBusy(false);
        }
    };

    const fmtTime = (iso) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleTimeString("en-US", {
                timeZone: "America/Edmonton",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            });
        } catch {
            return iso;
        }
    };

    const job = status?.job;
    const held = status?.held;
    const latestDraft = status?.latest_draft;
    const livePageHref = creator?.approved_at ? publicPageHref : null;
    const latestDraftHref = latestDraft?.id
        ? `/api/admin/preview/draft/${latestDraft.id}`
        : null;
    return (
        <section className="mb-8" data-testid="admin-creator-panel">
            <div className="samoony-label mb-3">Admin: this creator</div>
            <div className="bg-white border border-black/10 rounded-2xl px-5 py-4">
                <div className="flex flex-col gap-3">
                    <div className="min-w-0 w-full">
                        <div className="font-display text-base text-ink truncate">
                            {creator.first_name || creator.display_name || "Creator"}{" "}
                            <span className="text-xs text-black/50 font-sans">
                                /@{creator.samoony_handle}
                            </span>
                        </div>
                        <div className="font-mono text-[11px] text-black/50 break-all">
                            {creator.id}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                        <button
                            data-testid="admin-rebuild-page"
                            onClick={rebuild}
                            disabled={rebuilding}
                            className="samoony-pill samoony-pill-primary disabled:opacity-30 w-full"
                        >
                            {rebuilding ? "Starting..." : "Rebuild page"}
                        </button>
                        {creator?.approved_at && !creator?.is_demo && (
                            <Link
                                to={`/dashboard?view_as=${encodeURIComponent(creator.id)}`}
                                data-testid="admin-view-as-creator"
                                className="samoony-pill samoony-pill-ghost w-full text-center"
                            >
                                View as creator
                            </Link>
                        )}
                        {creator?.ownership_status === "pending_staff" && (
                            <button
                                data-testid="admin-approve-ownership"
                                onClick={async () => {
                                    if (!window.confirm(
                                        "Confirm this creator owns the handles they entered?"
                                    )) return;
                                    try {
                                        const res = await request(
                                            `/admin/creators/${creator.id}/approve-ownership`,
                                            { method: "POST" }
                                        );
                                        if (res?.build_started) {
                                            toast.success(
                                                "Ownership approved. Building their page now."
                                            );
                                        } else if (res?.already_has_page) {
                                            toast.success("Ownership approved.");
                                        } else {
                                            toast.success("Ownership approved.");
                                        }
                                        setTimeout(() => window.location.reload(), 400);
                                    } catch (e) {
                                        toast.error(
                                            (e && e.message) || "Could not approve."
                                        );
                                    }
                                }}
                                className="samoony-pill samoony-pill-primary w-full sm:col-span-2"
                            >
                                Approve ownership
                            </button>
                        )}
                    </div>
                </div>
                {job && (
                    <div
                        data-testid="admin-build-status"
                        className="font-mono text-[11px] text-black/60 mt-2"
                    >
                        {job.finished_at ? (
                            <>
                                Latest build: {job.stage || "?"} · finished {fmtTime(job.finished_at)}
                                {latestDraft?.version
                                    ? ` · draft v${latestDraft.version}`
                                    : ""}
                                {latestDraft?.created_at
                                    ? ` at ${fmtTime(latestDraft.created_at)}`
                                    : ""}
                            </>
                        ) : (
                            <>
                                job: running ({job.stage || "starting"})
                                {job.created_at
                                    ? ` · started ${fmtTime(job.created_at)}`
                                    : ""}
                            </>
                        )}
                        {job.error ? ` · ${String(job.error).slice(0, 120)}` : ""}
                    </div>
                )}
                {healthSubScores?.sub_scores && (
                    <div
                        data-testid="admin-health-sub-scores"
                        className="font-mono text-[11px] text-black/60 mt-1"
                    >
                        Brand health: score {healthSubScores.score ?? 0}/100 · named{" "}
                        {healthSubScores.sub_scores.named} · presence{" "}
                        {healthSubScores.sub_scores.presence} · freshness{" "}
                        {healthSubScores.sub_scores.freshness}
                    </div>
                )}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {livePageHref && (
                        <a
                            data-testid="admin-open-live-page"
                            href={livePageHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="samoony-pill samoony-pill-ghost"
                        >
                            Open live page
                        </a>
                    )}
                    {latestDraftHref && (
                        <a
                            data-testid="admin-open-latest-draft"
                            href={latestDraftHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="samoony-pill samoony-pill-ghost"
                        >
                            Open latest draft
                            {latestDraft?.version
                                ? ` (v${latestDraft.version})`
                                : ""}
                        </a>
                    )}
                </div>
            </div>
            <AdminSourcesSection creator={creator} />
            <AdminDirectoryListingsSection creator={creator} />
            <AdminPlacementsSection creator={creator} />
            {held && (
                <div
                    data-testid="admin-held-panel"
                    className="mt-3 border border-black/10 rounded-2xl p-4 bg-[color:var(--signal-tint,#fff5e1)]"
                >
                    <div className="font-display text-sm text-ink">
                        Update held: {held.reason}
                    </div>
                    <div className="font-mono text-[11px] text-black/60 mt-1">
                        Draft v{held.version} · {held.dropped_lines_count} dropped line
                        {held.dropped_lines_count === 1 ? "" : "s"}
                    </div>
                    {held.dropped_lines_count > 0 && (
                        <details className="mt-2">
                            <summary
                                className="font-mono text-[11px] text-black/60 cursor-pointer"
                                data-testid="admin-held-drops-toggle"
                            >
                                View dropped lines
                            </summary>
                            <ul className="mt-1 text-xs text-black/70 space-y-1">
                                {held.dropped_lines.map((d, i) => (
                                    <li key={i} className="border-l-2 border-black/20 pl-2">
                                        <span className="font-mono text-[10px] text-black/50">
                                            {d.section} · {d.reason}
                                        </span>
                                        <div>{d.text}</div>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                        <a
                            data-testid="admin-held-preview"
                            href={held.preview_path}
                            target="_blank"
                            rel="noreferrer"
                            className="samoony-pill samoony-pill-ghost"
                        >
                            Preview
                        </a>
                        <button
                            data-testid="admin-held-publish"
                            onClick={() => publishHeld(held.id)}
                            disabled={busy}
                            className="samoony-pill samoony-pill-primary disabled:opacity-30"
                        >
                            Publish anyway
                        </button>
                        <button
                            data-testid="admin-held-discard"
                            onClick={() => discardHeld(held.id)}
                            disabled={busy}
                            className="samoony-pill samoony-pill-ghost disabled:opacity-30"
                        >
                            Discard
                        </button>
                    </div>
                </div>
            )}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                    data-testid="admin-tracked-search-toggle"
                    onClick={() => setShowAdminTracked((v) => !v)}
                    className="samoony-pill samoony-pill-ghost w-full"
                >
                    Tracked searches {showAdminTracked ? "Hide" : "View"}
                </button>
                <button
                    data-testid="admin-handle-lookup-toggle"
                    onClick={() => setShowAdminHandle((v) => !v)}
                    className="samoony-pill samoony-pill-ghost w-full"
                >
                    Handle lookup {showAdminHandle ? "Hide" : "View"}
                </button>
            </div>
            {showAdminTracked && (
                <div className="mt-3">
                    <TrackedSearchPanel creatorId={creator.id} handle={creator.samoony_handle} />
                </div>
            )}
            {showAdminHandle && (
                <div className="mt-3">
                    <AdminHandleLookupPanel />
                </div>
            )}
        </section>
    );
}

export function AdminHandleLookupPanel() {
    const { request } = useApi();
    const [query, setQuery] = useState("");
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(false);
    const [releasingId, setReleasingId] = useState(null);

    const fmtDate = (iso) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleString("en-US", {
                timeZone: "America/Edmonton",
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
            });
        } catch {
            return iso;
        }
    };

    const lookup = async () => {
        const h = (query || "").trim().toLowerCase().replace(/^@/, "");
        if (!h) return;
        setBusy(true);
        try {
            const res = await request(`/admin/handle-lookup?handle=${encodeURIComponent(h)}`);
            setRows(res?.rows || []);
        } catch (err) {
            toast.error(err?.message || "Lookup failed.");
            setRows([]);
        } finally {
            setBusy(false);
        }
    };

    const release = async (row) => {
        if (!row?.releasable) return;
        setReleasingId(row.id);
        try {
            const res = await request(
                `/admin/handle-lookup/release/${row.id}`,
                { method: "POST" },
            );
            toast.success(
                `Renamed /@${res?.previous_samoony_handle || row.samoony_handle} to /@${res?.samoony_handle}.`,
            );
            await lookup();
        } catch (err) {
            toast.error(err?.message || "Release failed.");
        } finally {
            setReleasingId(null);
        }
    };

    return (
        <div
            data-testid="admin-handle-lookup"
            className="bg-white border border-black/10 rounded-2xl px-5 py-4"
        >
            <div className="samoony-label mb-2">Handle lookup</div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                <input
                    data-testid="handle-lookup-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="handle (e.g. coryatecha)"
                    className="font-mono text-sm border border-black/15 rounded-md px-3 py-2"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") lookup();
                    }}
                />
                <button
                    data-testid="handle-lookup-run"
                    onClick={lookup}
                    disabled={busy || !query.trim()}
                    className="samoony-pill samoony-pill-ghost disabled:opacity-30 w-full sm:w-auto"
                >
                    {busy ? "Looking up..." : "Look up"}
                </button>
            </div>
            {rows !== null && (
                <div className="mt-3" data-testid="handle-lookup-results">
                    {rows.length === 0 && (
                        <div
                            className="font-mono text-[12px] text-black/60"
                            data-testid="handle-lookup-empty"
                        >
                            Nothing holds /@{query.trim().toLowerCase().replace(/^@/, "")}.
                        </div>
                    )}
                    <ul className="space-y-2">
                        {rows.map((r) => (
                            <li
                                key={r.id}
                                data-testid={`handle-lookup-row-${r.id}`}
                                className="border border-black/10 rounded-xl px-3 py-2"
                            >
                                <div className="font-display text-sm text-ink">
                                    {r.name || "(no name)"}{" "}
                                    <span className="text-xs text-black/50 font-mono">
                                        /@{r.samoony_handle}
                                    </span>
                                </div>
                                <div className="font-mono text-[11px] text-black/60 break-all">
                                    kind: {r.kind} ·{" "}
                                    IG: {r.instagram_handle ? `@${r.instagram_handle}` : "—"} ·{" "}
                                    email: {r.email || "—"}
                                </div>
                                <div className="font-mono text-[11px] text-black/60">
                                    claimed: {r.claimed ? "yes" : "no"} ·{" "}
                                    approved: {r.approved ? "yes" : "no"} ·{" "}
                                    created: {r.created_at ? fmtDate(r.created_at) : "—"}
                                    {r.removed_at ? ` · removed: ${fmtDate(r.removed_at)}` : ""}
                                </div>
                                <div className="font-mono text-[11px] text-black/50 mt-1 break-all">
                                    id: {r.id}
                                </div>
                                {r.releasable ? (
                                    <div className="mt-2">
                                        <button
                                            data-testid={`handle-lookup-release-${r.id}`}
                                            onClick={() => release(r)}
                                            disabled={releasingId === r.id}
                                            className="samoony-pill samoony-pill-ghost disabled:opacity-30 w-full sm:w-auto"
                                        >
                                            {releasingId === r.id
                                                ? "Releasing..."
                                                : `Release handle (rename to /@${r.samoony_handle}-old)`}
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        data-testid={`handle-lookup-locked-${r.id}`}
                                        className="font-mono text-[11px] text-black/50 mt-2"
                                    >
                                        Approved real creator. Handle cannot be released.
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}


export function TrackedSearchPanel({ creatorId, handle }) {
    const { request } = useApi();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [edit, setEdit] = useState(false);
    const [draftQs, setDraftQs] = useState([]);
    const [placements, setPlacements] = useState("");
    const [cost, setCost] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [latestRun, setLatestRun] = useState(null);
    const [openRunId, setOpenRunId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await request(`/admin/tracked-search/${creatorId}`);
            setData(res || null);
            setDraftQs(res?.set?.questions || []);
            setPlacements((res?.set?.placement_urls || []).join("\n"));
            setLatestRun(null);
        } catch (err) {
            toast.error(err?.message || "Could not load tracked searches.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatorId]);

    const set = data?.set;
    const runs = data?.runs || [];

    const save = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const urls = placements
                .split(/\r?\n/)
                .map((s) => s.trim())
                .filter(Boolean);
            const res = await request(`/admin/tracked-search/${creatorId}/edit`, {
                method: "POST",
                body: { questions: draftQs, placement_urls: urls },
            });
            setData((prev) => ({ ...(prev || {}), set: res.set }));
            setEdit(false);
            toast.success("Saved.");
        } catch (err) {
            toast.error(err?.message || "Could not save.");
        } finally {
            setBusy(false);
        }
    };

    const openConfirm = async () => {
        try {
            const res = await request(`/admin/tracked-search/${creatorId}/cost`);
            setCost(res);
            setConfirmOpen(true);
        } catch (err) {
            toast.error(err?.message || "Could not get cost.");
        }
    };

    const runNow = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await request(
                `/admin/tracked-search/${creatorId}/run?confirm=1`,
                { method: "POST" }
            );
            setConfirmOpen(false);
            setLatestRun(res.run);
            await load();
            toast.success(
                `Named: ${res.run?.metrics?.named}/${res.run?.metrics?.answers} · Page cited: ${res.run?.metrics?.cited_samoony_page}`
            );
        } catch (err) {
            const detail = err?.detail;
            if (err?.status === 429 && detail && typeof detail === "object") {
                const started = detail.last_started_at
                    ? new Date(detail.last_started_at).toLocaleString("en-US", {
                        timeZone: "America/Edmonton",
                    })
                    : "recently";
                const mins = Math.ceil((detail.seconds_remaining || 0) / 60);
                toast.error(
                    `${detail.message || "Run blocked"} Last started ${started}. Try again in ~${mins} min.`
                );
            } else {
                toast.error(err?.message || "Run failed.");
            }
        } finally {
            setBusy(false);
        }
    };

    const updateQ = (i, field, value) => {
        setDraftQs((prev) => prev.map((q, k) => (k === i ? { ...q, [field]: value } : q)));
    };
    const removeQ = (i) => setDraftQs((prev) => prev.filter((_, k) => k !== i));
    const addQ = () => {
        if (draftQs.length >= 10) {
            toast.error("Maximum 10 questions.");
            return;
        }
        setDraftQs((prev) => [...prev, { text: "", level: "category", category: "general" }]);
    };

    return (
        <div
            data-testid={`tracked-search-${handle || creatorId}`}
            className="bg-white border border-black/10 rounded-2xl p-4"
        >
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="samoony-label">Tracked searches</div>
                <div className="grid grid-cols-1 sm:flex sm:items-center gap-2 w-full sm:w-auto flex-wrap">
                    {!edit && (
                        <button
                            data-testid="tracked-search-edit"
                            onClick={() => setEdit(true)}
                            disabled={busy}
                            className="samoony-pill samoony-pill-ghost disabled:opacity-30 w-full sm:w-auto"
                        >
                            Edit
                        </button>
                    )}
                    {edit && (
                        <button
                            data-testid="tracked-search-save"
                            onClick={save}
                            disabled={busy}
                            className="samoony-pill samoony-pill-ghost disabled:opacity-30 w-full sm:w-auto"
                        >
                            {busy ? "Saving..." : "Save"}
                        </button>
                    )}
                    <button
                        data-testid="tracked-search-run"
                        onClick={openConfirm}
                        disabled={busy}
                        className="samoony-pill disabled:opacity-30 w-full sm:w-auto"
                    >
                        {busy ? "Running" : "Run tracked searches now"}
                    </button>
                </div>
            </div>

            {loading && <div className="text-sm text-black/50 mt-2">Loading...</div>}
            {!loading && set && (
                <div className="mt-3 space-y-3">
                    <div className="font-mono text-[11px] text-black/60">
                        v{set.version} · month {set.month}
                    </div>
                    <div>
                        <div className="samoony-label mb-1">Questions</div>
                        {edit ? (
                            <ul className="space-y-2" data-testid="tracked-search-questions-edit">
                                {draftQs.map((q, i) => (
                                    <li
                                        key={i}
                                        className="flex items-center gap-2 flex-wrap"
                                    >
                                        <input
                                            data-testid={`tracked-q-text-${i}`}
                                            value={q.text}
                                            onChange={(e) => updateQ(i, "text", e.target.value)}
                                            placeholder="question"
                                            className="samoony-input flex-1 min-w-[200px] text-sm"
                                        />
                                        <select
                                            data-testid={`tracked-q-level-${i}`}
                                            value={q.level || "category"}
                                            onChange={(e) => updateQ(i, "level", e.target.value)}
                                            className="samoony-input w-28 text-xs"
                                        >
                                            <option value="broad">broad</option>
                                            <option value="category">category</option>
                                            <option value="direct">direct</option>
                                            <option value="local">local</option>
                                            <option value="brand">brand</option>
                                        </select>
                                        <input
                                            data-testid={`tracked-q-cat-${i}`}
                                            value={q.category}
                                            onChange={(e) => updateQ(i, "category", e.target.value)}
                                            placeholder="category"
                                            className="samoony-input w-32 font-mono text-xs"
                                        />
                                        <button
                                            data-testid={`tracked-q-remove-${i}`}
                                            onClick={() => removeQ(i)}
                                            className="samoony-pill samoony-pill-ghost"
                                        >
                                            Remove
                                        </button>
                                    </li>
                                ))}
                                <li>
                                    <button
                                        data-testid="tracked-q-add"
                                        onClick={addQ}
                                        className="samoony-pill samoony-pill-ghost"
                                    >
                                        Add question
                                    </button>
                                </li>
                            </ul>
                        ) : (
                            <ol className="space-y-1" data-testid="tracked-search-questions">
                                {(set.questions || []).map((q, i) => (
                                    <li key={i} className="text-sm text-ink">
                                        <span
                                            data-testid={`tracked-q-level-label-${i}`}
                                            className="font-mono text-[10px] uppercase mr-2 px-1.5 py-0.5 rounded bg-black/5 text-black/70"
                                        >
                                            {q.level || "category"}
                                        </span>
                                        <span className="font-mono text-[11px] text-black/50 mr-2">
                                            [{q.category || "general"}]
                                        </span>
                                        {q.text}
                                    </li>
                                ))}
                            </ol>
                        )}
                    </div>
                    <div>
                        <div className="samoony-label mb-1">Placement URLs</div>
                        {edit ? (
                            <textarea
                                data-testid="tracked-placements-edit"
                                value={placements}
                                onChange={(e) => setPlacements(e.target.value)}
                                rows={3}
                                placeholder="one URL per line"
                                className="samoony-input w-full font-mono text-xs"
                            />
                        ) : (
                            <ul data-testid="tracked-placements" className="space-y-1">
                                {(set.placement_urls || []).length === 0 ? (
                                    <li className="font-mono text-[11px] text-black/50">
                                        None recorded.
                                    </li>
                                ) : (
                                    (set.placement_urls || []).map((u, i) => (
                                        <li
                                            key={i}
                                            className="font-mono text-[11px] break-all"
                                        >
                                            {u}
                                        </li>
                                    ))
                                )}
                            </ul>
                        )}
                    </div>
                    <div
                        className="font-mono text-[11px] text-black/50"
                        data-testid="tracked-next-run"
                    >
                        Next run: {(() => {
                            const now = new Date();
                            const y = now.getFullYear();
                            const m = now.getMonth();
                            const d = now.getDate();
                            // Monthly cron fires on the 1st at 6am Edmonton
                            // (America/Edmonton = MST, UTC-7 standard /
                            //  MDT, UTC-6 daylight). Compute from today in
                            // local time; good enough for a status line.
                            let nextY = y;
                            let nextM = m + 1;
                            if (d === 1 && now.getHours() < 6) {
                                nextY = y;
                                nextM = m;
                            }
                            if (nextM > 11) {
                                nextM = 0;
                                nextY = y + 1;
                            }
                            const next = new Date(nextY, nextM, 1, 6, 0, 0);
                            return next.toLocaleString("en-US", {
                                timeZone: "America/Edmonton",
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                            });
                        })()} (Edmonton)
                    </div>

                    {latestRun && (
                        <RunSummary run={latestRun} testIdPrefix="tracked-latest-run" />
                    )}

                    {runs.length > 0 && (
                        <div>
                            <div className="samoony-label mb-1">Recent runs</div>
                            <ul className="space-y-1" data-testid="tracked-search-runs">
                                {runs.map((r) => (
                                    <li key={r.id}>
                                        <button
                                            data-testid={`tracked-run-open-${r.id}`}
                                            onClick={() =>
                                                setOpenRunId(openRunId === r.id ? null : r.id)
                                            }
                                            className="w-full text-left font-mono text-[11px] text-black/70 underline underline-offset-2 decoration-ink/30 hover:decoration-ink/60"
                                        >
                                            {new Date(r.run_date).toLocaleString("en-US", {
                                                timeZone: "America/Edmonton",
                                            })}
                                            {" · v"}{r.set_version} · named{" "}
                                            {r.metrics?.named}/{r.metrics?.answers} (
                                            {Math.round((r.metrics?.named_rate || 0) * 100)}%) ·
                                            page cited {r.metrics?.cited_samoony_page} · placement{" "}
                                            {r.metrics?.cited_placement}
                                            {r.metrics?.outcome
                                                ? ` · source ${r.metrics.outcome.source}/${r.metrics.outcome.total}`
                                                : ""}
                                            {openRunId === r.id ? " ▴" : " ▾"}
                                        </button>
                                        {openRunId === r.id && (
                                            <RunDetail
                                                creatorId={creatorId}
                                                runId={r.id}
                                            />
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {confirmOpen && (
                <div
                    data-testid="tracked-search-confirm"
                    className="mt-3 border border-black/15 rounded-xl p-3 bg-paper"
                >
                    <div className="text-sm">
                        This will run {cost?.questions || 0} questions ×{" "}
                        {(cost?.engines || []).length} engines ={" "}
                        <strong>{cost?.total_searches || 0}</strong> SerpApi searches.
                    </div>
                    <div className="font-mono text-[11px] text-black/60 mt-1">
                        Engines: {(cost?.engines || []).join(", ")}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                        <button
                            data-testid="tracked-search-confirm-cancel"
                            onClick={() => setConfirmOpen(false)}
                            disabled={busy}
                            className="samoony-pill samoony-pill-ghost disabled:opacity-30"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="tracked-search-confirm-run"
                            onClick={runNow}
                            disabled={busy}
                            className="samoony-pill disabled:opacity-30"
                        >
                            {busy ? "Running..." : "Confirm and run"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function RunDetail({ creatorId, runId }) {
    const { request } = useApi();
    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await request(
                    `/admin/tracked-search/${creatorId}/run/${runId}`,
                );
                if (!cancelled) setRun(res?.run || null);
            } catch (err) {
                if (!cancelled) toast.error(err?.message || "Could not load run.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatorId, runId]);

    if (loading) {
        return (
            <div
                data-testid={`tracked-run-detail-${runId}`}
                className="mt-2 text-xs text-black/50"
            >
                Loading run...
            </div>
        );
    }
    if (!run) return null;

    const _LEVEL_ORDER = ["broad", "category", "brand", "local", "direct"];
    const levelGrouped = new Map();
    for (const a of run.answers || []) {
        const lvl = (a.level || "category").toString();
        if (!levelGrouped.has(lvl)) levelGrouped.set(lvl, new Map());
        const g = levelGrouped.get(lvl);
        const key = a.question || "";
        if (!g.has(key)) g.set(key, []);
        g.get(key).push(a);
    }
    const sortedLevels = _LEVEL_ORDER.filter((l) => levelGrouped.has(l)).concat(
        [...levelGrouped.keys()].filter((l) => !_LEVEL_ORDER.includes(l)),
    );
    return (
        <div
            data-testid={`tracked-run-detail-${runId}`}
            className="mt-2 border border-black/15 rounded-xl p-3 bg-paper space-y-4"
        >
            {sortedLevels.map((lvl) => (
                <div
                    key={lvl}
                    data-testid={`tracked-run-level-${runId}-${lvl}`}
                    className="space-y-3"
                >
                    <div className="samoony-label uppercase text-black/70">
                        {lvl}
                    </div>
                    {Array.from(levelGrouped.get(lvl).entries()).map(([question, items], qi) => (
                        <div key={qi} className="space-y-2">
                            <div className="text-sm text-ink font-display">
                                {question || "(no question)"}
                            </div>
                            <div className="font-mono text-[10px] text-black/50">
                                Category: {items[0]?.category || "general"}
                            </div>
                            {items.map((a, i) => (
                                <div
                                    key={i}
                                    data-testid={`tracked-run-engine-${runId}-${lvl}-${qi}-${i}`}
                                    className="border-l-2 border-black/15 pl-2"
                                >
                                    <div className="font-mono text-[11px] text-black/70">
                                        {a.engine} ·{" "}
                                        <span
                                            className={
                                                a.creator_named ? "text-signal" : "text-black/60"
                                            }
                                        >
                                            named: {a.creator_named ? "yes" : "no"}
                                        </span>
                                        {a.cited_samoony_page ? " · page cited" : ""}
                                        {a.cited_placement_urls?.length ? " · placement cited" : ""}
                                        {a.cited_samoony_urls?.length ? " · samoony cited" : ""}
                                        {a.cited_own_urls?.length ? " · own post cited" : ""}
                                    </div>
                                    {Array.isArray(a.named_people) && a.named_people.length > 0 && (
                                        <div className="font-mono text-[11px] text-black/70 mt-0.5">
                                            Names found:{" "}
                                            {a.named_people
                                                .map((p) =>
                                                    p.handle
                                                        ? `${p.name} (@${p.handle})`
                                                        : p.name,
                                                )
                                                .join(", ")}
                                        </div>
                                    )}
                                    {Array.isArray(a.search_queries) && a.search_queries.length > 0 && (
                                        <div
                                            data-testid={`tracked-run-searches-${runId}-${lvl}-${qi}-${i}`}
                                            className="font-mono text-[11px] text-black/70 mt-0.5"
                                        >
                                            Searches it ran: {a.search_queries.join(" | ")}
                                        </div>
                                    )}
                                    {a.answer_text && (
                                        <div className="text-xs text-ink/80 mt-1 whitespace-pre-wrap">
                                            {a.answer_text}
                                        </div>
                                    )}
                                    {Array.isArray(a.cited_urls) && a.cited_urls.length > 0 && (
                                        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1">
                                            {a.cited_urls.map((u, j) => (
                                                <a
                                                    key={j}
                                                    href={u}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-mono text-[11px] underline decoration-signal underline-offset-2 break-all"
                                                >
                                                    {u}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function RunSummary({ run, testIdPrefix }) {
    const m = run?.metrics || {};
    const byCat = m.by_category || {};
    const byLevel = m.by_level || {};
    return (
        <div
            data-testid={testIdPrefix}
            className="border border-black/15 rounded-xl p-3 bg-paper"
        >
            <div className="samoony-label mb-1">Latest run</div>
            <div className="font-mono text-[11px] text-black/70">
                {new Date(run.run_date).toLocaleString("en-US", {
                    timeZone: "America/Edmonton",
                })}
            </div>
            <div className="text-sm mt-1">
                Named: <strong>{m.named}</strong> / {m.answers} (
                {Math.round((m.named_rate || 0) * 100)}%). Page cited:{" "}
                <strong>{m.cited_samoony_page}</strong>. Placement cited:{" "}
                <strong>{m.cited_placement}</strong>.
            </div>
            {m.outcome?.total > 0 && (
                <div className="text-sm mt-1" data-testid={`${testIdPrefix}-outcome`}>
                    Named in the answer: <strong>{m.outcome.named}</strong> / {m.outcome.total}.
                    Cited as a source: <strong>{m.outcome.source}</strong> / {m.outcome.total}.
                    Both: <strong>{m.outcome.both}</strong>.
                </div>
            )}
            {m.local?.total > 0 && (
                <div
                    className="text-sm mt-1"
                    data-testid={`${testIdPrefix}-local`}
                >
                    Local questions: <strong>{m.local.hit}</strong> / {m.local.total}{" "}
                    answers named the creator or cited Samoony or a placement (named{" "}
                    {m.local.named}, Samoony cited {m.local.cited_samoony}, placement
                    cited {m.local.cited_placement}). Not counted in the named rate.
                </div>
            )}
            {Object.keys(byLevel).length > 0 && (
                <div className="mt-2" data-testid={`${testIdPrefix}-by-level`}>
                    <div className="samoony-label mb-1">By level</div>
                    <ul className="space-y-0.5">
                        {["broad", "category", "brand", "local", "direct"]
                            .filter((lvl) => byLevel[lvl])
                            .map((lvl) => (
                                <li
                                    key={lvl}
                                    className="font-mono text-[11px] text-ink/80"
                                >
                                    {lvl}: {byLevel[lvl].named}/{byLevel[lvl].total} (
                                    {Math.round((byLevel[lvl].rate || 0) * 100)}%)
                                </li>
                            ))}
                    </ul>
                </div>
            )}
            <div className="mt-2">
                <div className="samoony-label mb-1">By category</div>
                <ul className="space-y-0.5">
                    {Object.entries(byCat).map(([k, v]) => (
                        <li key={k} className="font-mono text-[11px] text-ink/80">
                            {k}: {v.named}/{v.total} ({Math.round((v.rate || 0) * 100)}%)
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

// ---------------- Instagram read (prototype UI, not final design) ----------------

function urlById(chunks, id) {
    const match = (chunks || []).find((c) => c.id === id);
    return match ? match.url : null;
}

function Sources({ chunks, ids }) {
    const list = Array.isArray(ids) ? ids : [];
    if (list.length === 0) return null;
    return (
        <span className="font-mono text-[11px] text-black/50">
            {list.map((id, i) => {
                const url = urlById(chunks, id);
                return (
                    <span key={id}>
                        {i > 0 && ", "}
                        {url ? (
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline decoration-signal underline-offset-2"
                            >
                                    source {id}
                            </a>
                        ) : (
                            <>source {id}</>
                        )}
                    </span>
                );
            })}
        </span>
    );
}

function isNoData(item) {
    if (!item) return true;
    if (item.value === "no_data") return true;
    return false;
}

function SingleField({ label, item, chunks, testid }) {
    const empty = isNoData(item);
    return (
        <div data-testid={testid} className="py-3 border-b border-black/10 last:border-b-0">
            <div className="samoony-label mb-1">{label}</div>
            {empty ? (
                <div className="text-sm text-black/40">no data</div>
            ) : (
                <>
                    <div className="font-display text-base text-ink">{item.value}</div>
                    {item.quote && (
                        <div className="mt-1 text-xs text-black/60 italic">
                            "{item.quote}"
                        </div>
                    )}
                    <div className="mt-1">
                        <Sources chunks={chunks} ids={item.chunk_ids} />
                    </div>
                </>
            )}
        </div>
    );
}

function ArrayField({ label, items, chunks, testid }) {
    const list = Array.isArray(items) ? items : [];
    return (
        <div data-testid={testid} className="py-3 border-b border-black/10 last:border-b-0">
            <div className="samoony-label mb-2">{label}</div>
            {list.length === 0 ? (
                <div className="text-sm text-black/40">no data</div>
            ) : (
                <ul className="space-y-2">
                    {list.map((it, i) => (
                        <li key={i} className="text-sm">
                            <div className="font-display text-base text-ink">
                                {it.value}
                            </div>
                            {it.quote && (
                                <div className="mt-0.5 text-xs text-black/60 italic">
                                    "{it.quote}"
                                </div>
                            )}
                            <div className="mt-0.5">
                                <Sources chunks={chunks} ids={it.chunk_ids} />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function MostRecentPostField({ post }) {
    return (
        <div
            data-testid="read-most-recent-post"
            className="py-3 border-b border-black/10 last:border-b-0"
        >
            <div className="samoony-label mb-1">Most recent post</div>
            {!post ? (
                <div className="text-sm text-black/40">no data</div>
            ) : (
                <>
                    <a
                        href={post.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs underline decoration-signal underline-offset-2 break-all"
                    >
                        {post.url}
                    </a>
                    <div className="mt-1 text-xs text-black/60">
                        {post.date}
                    </div>
                    {post.excerpt && (
                        <div className="mt-1 font-display text-base text-ink break-words">
                            {post.excerpt}
                            {post.excerpt.length >= 140 ? "..." : ""}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function PlacesAndBrandsField({ items, chunks }) {
    const list = Array.isArray(items) ? items : [];
    const GROUPS = [
        { key: "restaurant_or_bar", label: "Restaurants and bars" },
        { key: "event", label: "Events" },
        { key: "chain_or_brand", label: "Chains and brands" },
        { key: "other", label: "Other" },
    ];
    const grouped = GROUPS.map((g) => ({
        ...g,
        rows: list.filter((it) => (it?.type || "other") === g.key),
    })).filter((g) => g.rows.length > 0);

    return (
        <div
            data-testid="read-places-and-brands"
            className="py-3 border-b border-black/10 last:border-b-0"
        >
            <div className="samoony-label mb-2">Places and brands</div>
            {list.length === 0 ? (
                <div className="text-sm text-black/40">no data</div>
            ) : (
                <div className="space-y-5">
                    {grouped.map((g) => (
                        <div key={g.key} data-testid={`places-group-${g.key}`}>
                            <div className="font-mono text-[11px] uppercase tracking-widest text-black/50 mb-2">
                                {g.label}
                            </div>
                            <ul className="space-y-3">
                                {g.rows.map((it, i) => {
                                    const tagOnly = it.evidence_strength === "tag_only";
                                    const matched = it.matched && it.matched_place;
                                    return (
                                        <li
                                            key={i}
                                            data-testid={`places-item-${g.key}-${i}`}
                                            className={`text-sm ${tagOnly ? "opacity-60" : ""}`}
                                        >
                                            <div className="flex items-baseline justify-between gap-3">
                                                <div className="font-display text-base text-ink break-words">
                                                    {it.name || it.value}
                                                    {it.descriptor && (
                                                        <span className="text-sm text-black/60 font-sans">
                                                            {" "}
                                                            ({it.descriptor})
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="font-mono text-[10px] uppercase tracking-widest text-black/40 shrink-0">
                                                    {tagOnly ? "tag only" : "visit"}
                                                    {it.city ? ` · ${it.city}` : ""}
                                                </span>
                                            </div>
                                            {it.handle && (
                                                <a
                                                    href={`https://www.instagram.com/${(it.handle || "").replace(/^@/, "")}/`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-mono text-[11px] text-black/60 underline decoration-signal underline-offset-2 break-all"
                                                >
                                                    {it.handle}
                                                </a>
                                            )}
                                            {matched && (
                                                <div className="mt-1 pl-3 border-l-2 border-signal/50">
                                                    <div className="font-display text-sm text-ink">
                                                        {it.matched_place.title}
                                                    </div>
                                                    {it.matched_place.address && (
                                                        <div className="text-xs text-black/60">
                                                            {it.matched_place.address}
                                                        </div>
                                                    )}
                                                    {it.matched_place.website && (
                                                        <a
                                                            href={it.matched_place.website}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="font-mono text-[11px] underline decoration-signal underline-offset-2 break-all"
                                                        >
                                                            {it.matched_place.website}
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                            {it.quote && (
                                                <div className="mt-1 text-xs text-black/60 italic">
                                                    "{it.quote}"
                                                </div>
                                            )}
                                            <div className="mt-1">
                                                <Sources chunks={chunks} ids={it.chunk_ids} />
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function LinkedProfilesField({ items, chunks }) {
    const list = Array.isArray(items) ? items : [];
    return (
        <div
            data-testid="read-linked-profiles"
            className="py-3 border-b border-black/10 last:border-b-0"
        >
            <div className="samoony-label mb-2">Linked profiles</div>
            {list.length === 0 ? (
                <div className="text-sm text-black/40">no data</div>
            ) : (
                <ul className="space-y-2">
                    {list.map((it, i) => (
                        <li key={i} className="text-sm flex items-center justify-between gap-3">
                            <a
                                href={it.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-display text-base text-ink underline decoration-signal underline-offset-4 break-all"
                            >
                                {it.url}
                            </a>
                            <span className="font-mono text-[11px] uppercase tracking-widest text-black/50 shrink-0">
                                {it.platform}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            <div className="mt-1">
                <Sources chunks={chunks} ids={list.length ? [1] : []} />
            </div>
        </div>
    );
}

function InstagramReadSection({ hasHandle }) {
    const { request } = useApi();
    const [read, setRead] = useState(null);
    const [job, setJob] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const startReadIdRef = useRef(null);

    // Initial fetch: previous read + latest job state.
    useEffect(() => {
        let active = true;
        request("/reads/instagram")
            .then((res) => {
                if (!active) return;
                if (res?.read) setRead(res.read);
                if (res?.job) {
                    setJob(res.job);
                    if (res.job.status === "running") {
                        startReadIdRef.current = res?.read?.id || null;
                        setRunning(true);
                    }
                }
            })
            .catch(() => {})
            .finally(() => active && setInitialLoading(false));
        return () => {
            active = false;
        };
    }, [request]);

    // Poll while running: every 3s, up to 3 minutes.
    useEffect(() => {
        if (!running) return;
        const deadline = Date.now() + 3 * 60 * 1000;
        let cancelled = false;
        let timer = null;

        const stop = () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };

        const tick = async () => {
            if (cancelled) return;
            try {
                const res = await request("/reads/instagram");
                if (cancelled) return;
                if (res?.job) setJob(res.job);
                const newRead = res?.read;
                if (newRead && newRead.id !== startReadIdRef.current) {
                    setRead(newRead);
                    setRunning(false);
                    toast.success("Profile built.");
                    stop();
                    return;
                }
                if (res?.job && res.job.status === "failed") {
                    setRunning(false);
                    toast.error("Something went wrong. Try again in a minute.");
                    stop();
                    return;
                }
                if (Date.now() >= deadline) {
                    setRunning(false);
                    toast.error("Something went wrong. Try again in a minute.");
                    stop();
                    return;
                }
                timer = setTimeout(tick, 3000);
            } catch {
                if (cancelled) return;
                setRunning(false);
                toast.error("Something went wrong. Try again in a minute.");
                stop();
            }
        };
        timer = setTimeout(tick, 3000);
        return stop;
    }, [running, request]);

    const build = async () => {
        if (running) return;
        startReadIdRef.current = read?.id || null;
        setRunning(true);
        try {
            await request("/reads/instagram", { method: "POST" });
        } catch {
            setRunning(false);
            toast.error("Something went wrong. Try again in a minute.");
        }
    };

    const output = read?.output || null;
    const chunks = read?.chunks || [];

    return (
        <section className="mt-8" data-testid="instagram-read-section">
            <div className="samoony-label mb-3">Admin: profile builder</div>
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="text-xs text-black/60">
                    {hasHandle
                        ? "Builds a profile from your recent posts."
                        : "Add an Instagram handle in step 1 to use this."}
                </div>
                <button
                    data-testid="instagram-read-run"
                    onClick={build}
                    disabled={!hasHandle || running}
                    className="samoony-pill samoony-pill-primary disabled:opacity-30"
                >
                    {running ? (
                        <>
                            <Loader2 size={16} className="animate-spin" />
                            Building your profile...
                        </>
                    ) : (
                        <>
                            <PlayCircle size={16} /> Build profile
                        </>
                    )}
                </button>
            </div>

            {initialLoading && (
                <div className="text-sm text-black/50">Loading...</div>
            )}

            {!initialLoading && !output && !running && (
                <div
                    data-testid="instagram-read-empty"
                    className="bg-white border border-black/10 rounded-2xl p-5 text-sm text-black/60"
                >
                    No profile built yet.
                </div>
            )}

            {output && (
                <div
                    data-testid="instagram-read-output"
                    className="bg-white border border-black/10 rounded-2xl px-5 sm:px-6 py-2"
                >
                    <SingleField
                        label="Summary line"
                        item={output.summary_line}
                        chunks={chunks}
                        testid="read-summary-line"
                    />
                    <SingleField
                        label="Niche"
                        item={output.niche}
                        chunks={chunks}
                        testid="read-niche"
                    />
                    <SingleField
                        label="City"
                        item={output.city}
                        chunks={chunks}
                        testid="read-city"
                    />
                    <ArrayField
                        label="Topics"
                        items={output.topics}
                        chunks={chunks}
                        testid="read-topics"
                    />
                    <ArrayField
                        label="Format and tone"
                        items={output.format_and_tone}
                        chunks={chunks}
                        testid="read-format-and-tone"
                    />
                    <SingleField
                        label="Audience"
                        item={output.audience}
                        chunks={chunks}
                        testid="read-audience"
                    />
                    <ArrayField
                        label="Series"
                        items={output.series}
                        chunks={chunks}
                        testid="read-series"
                    />
                    <ArrayField
                        label="Featured posts"
                        items={output.featured_posts}
                        chunks={chunks}
                        testid="read-featured-posts"
                    />
                    <PlacesAndBrandsField
                        items={output.places_and_brands}
                        chunks={chunks}
                    />
                    <ArrayField
                        label="People mentioned"
                        items={output.people_mentioned}
                        chunks={chunks}
                        testid="read-people-mentioned"
                    />
                    <LinkedProfilesField
                        items={output.linked_profiles}
                        chunks={chunks}
                    />
                    <MostRecentPostField post={output.most_recent_post} />
                    <div className="py-3 font-mono text-[11px] text-black/40">
                        {read.chunks?.length || 0} sources, generated{" "}
                        {new Date(read.fetched_at).toLocaleString()}
                    </div>
                </div>
            )}
        </section>
    );
}

// ---------------- Page draft (admin) ----------------
function SourcesInline({ ids }) {
    const list = Array.isArray(ids) ? ids : [];
    if (list.length === 0) return <span className="font-mono text-[10px] text-black/40">sources: none</span>;
    return (
        <span className="font-mono text-[10px] text-black/40">
            sources: {list.map((id) => `#${id}`).join(", ")}
        </span>
    );
}

function PageDraftSection() {
    const { request } = useApi();
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    useEffect(() => {
        let active = true;
        request("/pages/draft")
            .then((res) => {
                if (!active) return;
                if (res?.draft) setDraft(res.draft);
            })
            .catch(() => {})
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [request]);

    const write = async () => {
        if (running) return;
        setRunning(true);
        try {
            const res = await request("/pages/draft", { method: "POST" });
            if (res?.draft) {
                setDraft(res.draft);
                if (res.draft.publishable) toast.success("Page draft written.");
                else toast(res.draft.gate_reason || "Not publishable yet.");
            }
        } catch {
            toast.error("Something went wrong. Try again in a minute.");
        } finally {
            setRunning(false);
        }
    };

    const sections = draft?.sections || null;

    return (
        <section className="mt-8" data-testid="page-draft-section">
            <div className="samoony-label mb-3">Admin: page draft</div>
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="text-xs text-black/60">
                    Writes a draft page from the latest profile build.
                </div>
                <button
                    data-testid="page-draft-run"
                    onClick={write}
                    disabled={running}
                    className="samoony-pill samoony-pill-primary disabled:opacity-30"
                >
                    {running ? (
                        <>
                            <Loader2 size={16} className="animate-spin" />
                            Writing...
                        </>
                    ) : (
                        <>
                            <PlayCircle size={16} /> Write page
                        </>
                    )}
                </button>
            </div>

            {loading && <div className="text-sm text-black/50">Loading...</div>}

            {!loading && !draft && (
                <div
                    data-testid="page-draft-empty"
                    className="bg-white border border-black/10 rounded-2xl p-5 text-sm text-black/60"
                >
                    No page draft yet.
                </div>
            )}

            {draft && !draft.publishable && (
                <div
                    data-testid="page-draft-blocked"
                    className="bg-white border border-black/10 rounded-2xl p-5 text-sm text-black/70"
                >
                    <div className="samoony-label mb-1">Not publishable yet</div>
                    <div>{draft.gate_reason}</div>
                    <div className="mt-2 font-mono text-[10px] text-black/40">
                        version {draft.version}, saved {new Date(draft.created_at).toLocaleString()}
                    </div>
                </div>
            )}

            {draft && draft.publishable && sections && (
                <div
                    data-testid="page-draft-output"
                    className="bg-white border border-black/10 rounded-2xl px-5 sm:px-6 py-2 space-y-5"
                >
                    <DraftSection label="Hero" sources={sections.hero?.chunk_ids}>
                        <div className="font-display text-2xl text-ink break-words">
                            {sections.hero?.name || sections.hero?.display_name}
                        </div>
                        <div className="font-mono text-xs text-black/60">
                            @{sections.hero?.handle}
                        </div>
                        <div className="font-display text-base text-ink mt-1 break-words">
                            {sections.hero?.one_line}
                        </div>
                    </DraftSection>

                    <DraftSection label="About" sources={sections.about?.chunk_ids}>
                        {(sections.about?.paragraphs || []).map((p, i) => (
                            <p key={i} className="text-sm text-ink mb-2 break-words">
                                {p}
                            </p>
                        ))}
                    </DraftSection>

                    {(sections.fit?.lines || []).length > 0 && (
                        <DraftSection label="Fit" sources={sections.fit?.chunk_ids}>
                            {sections.fit.lines.map((line, i) => (
                                <p key={i} className="text-sm text-ink mb-2 break-words">
                                    {line}
                                </p>
                            ))}
                        </DraftSection>
                    )}

                    <DraftSection label="Best for" sources={sections.best_for?.chunk_ids}>
                        <div className="font-display text-base text-ink break-words">
                            {sections.best_for?.line}
                        </div>
                    </DraftSection>

                    <DraftSection label="Topics" sources={sections.topics?.chunk_ids}>
                        <div className="flex flex-wrap gap-2">
                            {(sections.topics?.labels || []).map((t, i) => (
                                <span key={i} className="samoony-chip !min-h-0 py-1">
                                    {t}
                                </span>
                            ))}
                        </div>
                    </DraftSection>

                    <DraftSection label="Places covered">
                        <PlacesCoveredView places={sections.places_covered} />
                    </DraftSection>

                    <DraftSection label="Featured posts">
                        <ul className="space-y-3">
                            {(sections.featured_posts || []).map((f, i) => (
                                <li key={i} className="text-sm">
                                    <div className="font-display text-base text-ink break-words">
                                        {f.description}
                                    </div>
                                    {f.post_url && (
                                        <a
                                            href={f.post_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-[11px] underline decoration-signal underline-offset-2 break-all"
                                        >
                                            {f.post_url}
                                        </a>
                                    )}
                                    <div className="mt-0.5">
                                        <SourcesInline ids={f.chunk_ids} />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </DraftSection>

                    <DraftSection label="What's new">
                        <ul className="space-y-3">
                            {(sections.whats_new || []).map((n, i) => (
                                <li key={i} className="text-sm">
                                    <div className="font-mono text-[11px] text-black/50">
                                        {n.date}
                                    </div>
                                    <div className="font-display text-base text-ink break-words">
                                        {n.line}
                                    </div>
                                    {n.post_url && (
                                        <a
                                            href={n.post_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-[11px] underline decoration-signal underline-offset-2 break-all"
                                        >
                                            {n.post_url}
                                        </a>
                                    )}
                                    <div className="mt-0.5">
                                        <SourcesInline ids={n.chunk_ids} />
                                    </div>
                                </li>
                            ))}
                            {(sections.whats_new || []).length === 0 && (
                                <li className="text-sm text-black/40">no data</li>
                            )}
                        </ul>
                    </DraftSection>

                    <DraftSection label="FAQ">
                        <ul className="space-y-4">
                            {(sections.faq || []).map((f, i) => (
                                <li key={i} className="text-sm">
                                    <div className="font-display text-base text-ink break-words">
                                        {f.question}
                                    </div>
                                    <div className="mt-1 text-sm text-black/80 break-words">
                                        {f.answer}
                                    </div>
                                    <div className="mt-1">
                                        <SourcesInline ids={f.chunk_ids} />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </DraftSection>

                    <DraftSection label="Linked profiles">
                        <ul className="space-y-1">
                            {(sections.linked_profiles || []).map((lp, i) => (
                                <li key={i} className="text-sm flex items-center justify-between gap-3">
                                    <a
                                        href={lp.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-mono text-xs underline decoration-signal underline-offset-2 break-all"
                                    >
                                        {lp.url}
                                    </a>
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-black/50 shrink-0">
                                        {lp.platform}
                                    </span>
                                </li>
                            ))}
                            {(sections.linked_profiles || []).length === 0 && (
                                <li className="text-sm text-black/40">no data</li>
                            )}
                        </ul>
                    </DraftSection>

                    <div className="py-3 font-mono text-[11px] text-black/40">
                        version {draft.version}, saved {new Date(draft.created_at).toLocaleString()}
                    </div>
                </div>
            )}
        </section>
    );
}

function DraftSection({ label, sources, children }) {
    return (
        <div className="py-3 border-b border-black/10 last:border-b-0">
            <div className="samoony-label mb-2">{label}</div>
            {children}
            {Array.isArray(sources) && (
                <div className="mt-2">
                    <SourcesInline ids={sources} />
                </div>
            )}
        </div>
    );
}

function PlacesCoveredView({ places }) {
    if (!places) return <div className="text-sm text-black/40">no data</div>;
    const { primary_city, primary, also_covered, events } = places;
    return (
        <div className="space-y-4">
            {primary && primary.length > 0 && (
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-black/50 mb-2">
                        {primary_city}
                    </div>
                    <PlacesList items={primary} />
                </div>
            )}
            {also_covered && also_covered.length > 0 && (
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-black/50 mb-2">
                        Also covered
                    </div>
                    <PlacesList items={also_covered} />
                </div>
            )}
            {events && events.length > 0 && (
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-black/50 mb-2">
                        Events
                    </div>
                    <PlacesList items={events} />
                </div>
            )}
            {(!primary || primary.length === 0) &&
                (!also_covered || also_covered.length === 0) &&
                (!events || events.length === 0) && (
                    <div className="text-sm text-black/40">no data</div>
                )}
        </div>
    );
}

function PlacesList({ items }) {
    return (
        <ul className="space-y-3">
            {items.map((it, i) => (
                <li key={i} className="text-sm">
                    <div className="font-display text-base text-ink break-words">
                        {it.name}
                        {it.city && (
                            <span className="text-sm text-black/60 font-sans"> · {it.city}</span>
                        )}
                    </div>
                    {it.address && (
                        <div className="text-xs text-black/60 break-words">
                            {it.address}
                        </div>
                    )}
                    {it.post_url && (
                        <a
                            href={it.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-[11px] underline decoration-signal underline-offset-2 break-all"
                        >
                            {it.post_url}
                        </a>
                    )}
                    <div className="mt-0.5">
                        <SourcesInline ids={it.chunk_ids} />
                    </div>
                </li>
            ))}
        </ul>
    );
}

export function RescoreAnswersTile() {
    const { request } = useApi();
    const [busy, setBusy] = useState(false);

    const run = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await request("/admin/leader-lookup/rescore", {
                method: "POST",
            });
            const flips = res?.flips_by_date || {};
            const parts = Object.keys(flips)
                .sort()
                .map((d) => `${d}: ${flips[d]}`);
            toast.success(
                `Rescored ${res?.scanned || 0} answers. Flipped ${res?.flipped_total || 0}.`
                + (parts.length ? ` (${parts.join(", ")})` : ""),
            );
        } catch (err) {
            toast.error(err?.message || "Could not rescore.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="admin-rescore-answers-tile"
            className="bg-white border border-black/10 rounded-2xl p-5"
        >
            <div className="samoony-label mb-2">Rescore answers</div>
            <p className="text-sm text-black/60 mb-3">
                Re-check every tracked answer against the current scoring rules
                and flip any that now resolve differently.
            </p>
            <button
                data-testid="admin-rescore-answers-run"
                onClick={run}
                disabled={busy}
                className="samoony-chip disabled:opacity-30"
            >
                {busy ? "Rescoring..." : "Rescore now"}
            </button>
        </div>
    );
}

// ---------------- Admin: Demo build ----------------
export function DemoBuildSection() {
    const { request } = useApi();
    const [handle, setHandle] = useState("");
    const [firstName, setFirstName] = useState("");
    const [city, setCity] = useState("");
    const [primary, setPrimary] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [demos, setDemos] = useState([]);
    const [loading, setLoading] = useState(true);
    const timerRef = useRef(null);
    const activeRef = useRef(true);

    const refresh = useCallback(async () => {
        if (!activeRef.current) return;
        try {
            const res = await request("/admin/demo/list");
            if (!activeRef.current) return;
            setDemos(res?.demos || []);
            const anyRunning = (res?.demos || []).some((x) => x.job_running);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(refresh, anyRunning ? 3000 : 15000);
        } catch {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(refresh, 15000);
        } finally {
            if (activeRef.current) setLoading(false);
        }
    }, [request]);

    const load = refresh;

    useEffect(() => {
        activeRef.current = true;
        refresh();
        return () => {
            activeRef.current = false;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [refresh]);

    const run = async () => {
        if (submitting) return;
        const ig = handle.trim().replace(/^@/, "");
        if (!ig) {
            toast.error("Instagram handle is required.");
            return;
        }
        if (!primary.trim()) {
            toast.error("Primary niche is required.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await request("/admin/demo/build", {
                method: "POST",
                body: {
                    instagram_handle: ig,
                    first_name: firstName.trim() || null,
                    city: city.trim() || null,
                    primary_niche: primary.trim(),
                },
            });
            toast.success("Demo build started.");
            setHandle("");
            setFirstName("");
            setCity("");
            setPrimary("");
            await load();
            void res;
        } catch (err) {
            toast.error(err?.message || "Could not start demo build.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="mt-8" data-testid="demo-build-section">
            <div className="samoony-label mb-3">Admin: demo build</div>
            <div className="bg-white border border-black/10 rounded-2xl px-5 sm:px-6 py-4">
                <div className="text-xs text-black/60 mb-3">
                    Build a demo page from any public Instagram handle. No limits, never indexed.
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                        data-testid="demo-build-handle"
                        value={handle}
                        onChange={(e) => setHandle(e.target.value)}
                        placeholder="Instagram handle (required)"
                        className="samoony-input"
                    />
                    <input
                        data-testid="demo-build-first-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First name (optional)"
                        className="samoony-input"
                    />
                    <input
                        data-testid="demo-build-city"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="City (optional)"
                        className="samoony-input"
                    />
                    <select
                        data-testid="demo-build-primary-niche"
                        value={primary}
                        onChange={(e) => setPrimary(e.target.value)}
                        className="samoony-input"
                    >
                        <option value="">Primary niche (required)</option>
                        <option value="Food">Food</option>
                        <option value="Fitness">Fitness</option>
                        <option value="Beauty">Beauty</option>
                        <option value="Travel">Travel</option>
                        <option value="Local life">Local life</option>
                        <option value="Other">Other</option>
                    </select>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        data-testid="demo-build-run"
                        onClick={run}
                        disabled={submitting || !handle.trim() || !primary.trim()}
                        className="samoony-pill samoony-pill-primary disabled:opacity-30"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={16} className="animate-spin" /> Starting...
                            </>
                        ) : (
                            <>
                                <PlayCircle size={16} /> Start demo build
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="flex flex-col mt-6 mb-2 gap-2">
                <div className="samoony-label">Recent demo builds</div>
            </div>
            {loading && <div className="text-sm text-black/50">Loading...</div>}
            {!loading && demos.length === 0 && (
                <div className="text-sm text-black/50" data-testid="demo-build-empty">
                    No demo builds yet.
                </div>
            )}
            {!loading && demos.length > 0 && (
                <ul data-testid="demo-build-list" className="bg-white border border-black/10 rounded-2xl divide-y divide-black/10">
                    {demos.map((d) => (
                        <DemoRow key={d.creator_id} demo={d} onChanged={load} />
                    ))}
                </ul>
            )}
        </section>
    );
}

function DemoRow({ demo: d, onChanged }) {
    const { request } = useApi();
    const [showDrops, setShowDrops] = useState(false);
    const [rebuilding, setRebuilding] = useState(false);
    const [showTracked, setShowTracked] = useState(false);
    const [creatingPlacements, setCreatingPlacements] = useState(false);
    const createPlacements = async () => {
        if (creatingPlacements) return;
        setCreatingPlacements(true);
        try {
            const res = await request(`/admin/placement/create/${d.creator_id}`, {
                method: "POST",
            });
            if (res?.created > 0) {
                toast.success(`Created ${res.created} placement tasks.`);
            } else {
                toast.message(res?.message || "Tasks already exist.");
            }
        } catch (err) {
            toast.error(err?.message || "Could not create tasks.");
        } finally {
            setCreatingPlacements(false);
        }
    };
    const fmtTime = (iso) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                timeZone: "America/Edmonton",
            });
        } catch {
            return iso;
        }
    };
    const draftLine = (() => {
        const parts = [];
        if (d.draft_version) parts.push(`Draft v${d.draft_version}`);
        const built = fmtTime(d.draft_created_at);
        if (built) parts.push(`built ${built}`);
        let main = parts.join(", ") || (d.has_draft ? "Draft ready" : "No draft yet");
        let status;
        if (d.job_running) {
            status = `job: running (${d.stage || "starting"})`;
        } else {
            status = `job: ${d.stage || "unknown"}`;
            const finished = fmtTime(d.job_finished_at);
            if (finished) status += ` at ${finished}`;
        }
        return `${main} · ${status}`;
    })();

    const rebuild = async () => {
        if (rebuilding) return;
        setRebuilding(true);
        try {
            await request("/admin/demo/build", {
                method: "POST",
                body: {
                    instagram_handle: d.instagram_handle,
                    first_name: d.first_name || null,
                    city: d.city || null,
                    primary_niche: d.primary_niche || null,
                },
            });
            toast.success("Rebuild started.");
            // Immediately refresh so the row flips to running state and the
            // polling loop in the parent kicks in.
            if (typeof onChanged === "function") {
                try { await onChanged(); } catch { /* noop */ }
            }
        } catch (err) {
            toast.error(err?.message || "Could not start rebuild.");
        } finally {
            setRebuilding(false);
        }
    };

    const [renamingHandle, setRenamingHandle] = useState(false);

    const useExactHandle = async () => {
        if (renamingHandle) return;
        setRenamingHandle(true);
        try {
            const res = await request(
                `/admin/demo/${d.creator_id}/use-exact-handle`,
                { method: "POST" },
            );
            if (res?.unchanged) {
                toast.success("Handle already matches.");
            } else {
                toast.success(
                    `Renamed /@${res?.previous_samoony_handle || d.samoony_handle} to /@${res?.samoony_handle}.`,
                );
            }
            if (typeof onChanged === "function") {
                try { await onChanged(); } catch { /* noop */ }
            }
        } catch (err) {
            const detail = err?.detail || err;
            if (detail && typeof detail === "object" && detail.message) {
                const created = detail.created_at
                    ? ` (created ${new Date(detail.created_at).toLocaleString(
                        "en-US", { timeZone: "America/Edmonton" }
                    )})`
                    : "";
                toast.error(`${detail.message}${created}`);
            } else {
                toast.error(err?.message || "Could not rename handle.");
            }
        } finally {
            setRenamingHandle(false);
        }
    };

    return (
        <li className="px-5 sm:px-6 py-3 gap-3">
            <div className="flex flex-col gap-3">
                <div className="min-w-0 w-full">
                    <div className="font-display text-base text-ink truncate">
                        @{d.instagram_handle}{" "}
                        <span className="text-xs text-black/50 font-sans">
                            /@{d.samoony_handle}
                        </span>
                    </div>
                    <div
                        data-testid={`demo-build-status-${d.samoony_handle}`}
                        className="font-mono text-[11px] text-black/60"
                    >
                        {draftLine}
                    </div>
                    {d.read_suggested_niche && (
                        <div
                            data-testid={`demo-build-niche-note-${d.samoony_handle}`}
                            className="font-mono text-[11px] text-signal mt-0.5"
                        >
                            Read suggests: {d.read_suggested_niche}
                        </div>
                    )}
                    {typeof d.dropped_lines_count === "number" && d.dropped_lines_count > 0 && (
                        <button
                            data-testid={`demo-build-drops-toggle-${d.samoony_handle}`}
                            onClick={() => setShowDrops((v) => !v)}
                            className="font-mono text-[11px] text-ink/70 underline underline-offset-2 mt-0.5"
                        >
                            Safety drops: {d.dropped_lines_count}
                            {" "}· {showDrops ? "Hide" : "View"}
                        </button>
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                    <button
                        data-testid={`tracked-search-toggle-${d.samoony_handle}`}
                        onClick={() => setShowTracked((v) => !v)}
                        className="font-mono text-[11px] text-ink/70 underline underline-offset-2 text-left"
                    >
                        Tracked searches {showTracked ? "Hide" : "View"}
                    </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                    <button
                        data-testid={`demo-create-placements-${d.samoony_handle}`}
                        onClick={createPlacements}
                        disabled={creatingPlacements}
                        className="samoony-pill samoony-pill-ghost disabled:opacity-30 w-full"
                    >
                        {creatingPlacements ? "Creating..." : "Create placement tasks"}
                    </button>
                    {d.samoony_handle !== d.instagram_handle && (
                        <button
                            data-testid={`demo-use-exact-handle-${d.samoony_handle}`}
                            onClick={useExactHandle}
                            disabled={renamingHandle}
                            className="samoony-pill samoony-pill-ghost disabled:opacity-30 w-full"
                            title={`Rename /@${d.samoony_handle} to /@${d.instagram_handle}`}
                        >
                            {renamingHandle ? "Renaming..." : "Use exact handle"}
                        </button>
                    )}
                    <button
                        data-testid={`demo-build-rebuild-${d.samoony_handle}`}
                        onClick={rebuild}
                        disabled={rebuilding}
                        className="samoony-pill samoony-pill-ghost disabled:opacity-30 w-full"
                    >
                        {rebuilding ? "Starting..." : "Rebuild"}
                    </button>
                    <a
                        data-testid={`demo-build-preview-${d.samoony_handle}`}
                        href={d.preview_path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="samoony-pill text-center w-full"
                    >
                        Preview
                    </a>
                </div>
            </div>
            {showDrops && Array.isArray(d.dropped_lines) && d.dropped_lines.length > 0 && (
                <ul
                    data-testid={`demo-build-drops-list-${d.samoony_handle}`}
                    className="mt-3 bg-black/[0.04] rounded-lg p-3 space-y-1.5"
                >
                    {d.dropped_lines.map((drop, i) => (
                        <li key={i} className="font-mono text-[11px] text-ink/80">
                            <span className="text-signal">{drop.reason}</span>{" · "}
                            <span className="text-black/50">{drop.section}</span>{" — "}
                            <span>{drop.text}</span>
                        </li>
                    ))}
                </ul>
            )}
            {showTracked && (
                <div className="mt-3">
                    <TrackedSearchPanel creatorId={d.creator_id} handle={d.samoony_handle} />
                </div>
            )}
        </li>
    );
}
